import React, { useState, useEffect } from 'react';
import './PropertyDetail.css';
import PropertyDocuments from './PropertyDocuments';
import RoomDetail from './RoomDetail';
import ChatConversation from './ChatConversation';
import { supabase } from '../supabaseClient';
import PaymentHistory from '../components/PaymentHistory';
import ExpenseSummary from './ExpenseSummary';
import { InvestmentForm } from '../components/InvestmentForm';
import MonthNavigator from '../components/MonthNavigator';
import MonthlyHistoryCard, { monthIndex, getTenantStartIndex, earliestMonth } from '../components/MonthlyHistoryCard';
import { calcRetroMonths, isMonthBefore, buildRetroStartDate, defaultRetroMonth } from '../utils/expenseRetroactive';
import { calcRemainingPayments, calcLastPaymentLabel } from '../utils/expenseDuration';

function isFutureMonth(year, month) {
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth());
}

function sameMonthYear(date, year, month) {
  const d = new Date(date);
  return d.getFullYear() === year && d.getMonth() === month;
}

function getExpensesForMonth(expenses, year, month) {
  return expenses.filter(e => {
    // Gastos únicos: aparecer solo en su mes exacto
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    // Gastos recurrentes: start_date marca el inicio de la recurrencia
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    // Gastos temporales: mostrar todos los meses dentro del span total
    if (e.type === 'recurrente_temporal') {
      if (monthsDiff >= (e.duration_payments || 0) * step) return false;
    }
    // Meses saltados
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getMonthlyEquivalent(expense) {
  const amt = Number(expense.amount) || 0;
  if (expense.frequency === 'trimestral') return amt / 3;
  if (expense.frequency === 'anual') return amt / 12;
  if (expense.frequency === 'custom') return amt / (expense.custom_frequency_months || 1);
  return amt;
}

// Vista real: solo muestra el gasto en su mes de pago, con el importe completo
function getExpensesForMonthReal(expenses, year, month) {
  return expenses.filter(e => {
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    if (monthsDiff % step !== 0) return false;
    if (e.type === 'recurrente_temporal') {
      const paymentIndex = monthsDiff / step;
      if (paymentIndex >= (e.duration_payments || 0)) return false;
    }
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getAmountForView(expense, view) {
  if (view === 'real') return Number(expense.amount) || 0;
  return getMonthlyEquivalent(expense);
}

// Devuelve el step en meses según la frecuencia del gasto
function getFrequencyStep(expense) {
  if (expense.frequency === 'trimestral') return 3;
  if (expense.frequency === 'anual') return 12;
  if (expense.frequency === 'custom') return expense.custom_frequency_months || 1;
  return 1;
}

// Calcula la fecha de inicio del período al que pertenece el mes (year, month)
// Devuelve string "YYYY-MM-01" o null si no hay start_date
function getCurrentPeriodStart(expense, year, month) {
  if (!expense.start_date) return null;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  const periodIndex = Math.floor(monthsDiff / step);
  const totalStartMonths = sy * 12 + sm + periodIndex * step;
  const pYear = Math.floor(totalStartMonths / 12);
  const pMonth = totalStartMonths % 12;
  return `${pYear}-${String(pMonth + 1).padStart(2, '0')}-01`;
}

// Devuelve true si el mes NO tiene importe introducido para el período actual
function isExpensePendingVariable(expense, year, month) {
  if (expense.type !== 'recurrente_variable') return false;
  if (expense.frequency === 'manual') return false;
  const currentPeriodStart = getCurrentPeriodStart(expense, year, month);
  if (!currentPeriodStart) return false;
  const savedPeriodStart = expense.period_start
    ? expense.period_start.substring(0, 10)
    : null;
  return savedPeriodStart !== currentPeriodStart;
}

// Devuelve true si el mes es el mes de pago real (para el badge visual)
function isPaymentMonth(expense, year, month) {
  if (!expense.start_date) return true;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  return monthsDiff % step === 0;
}

function calcPaymentsMade(expense) {
  if (!expense.start_date) return expense.payments_made || 0;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const now = new Date();
  const sy = start.getFullYear(), sm = start.getMonth();
  const ny = now.getFullYear(), nm = now.getMonth();
  const step = expense.frequency === 'trimestral' ? 3 : expense.frequency === 'anual' ? 12 : expense.frequency === 'custom' ? (expense.custom_frequency_months || 1) : 1;
  const skipped = expense.skipped_months || [];
  let made = 0;
  let y = sy, m = sm;
  while (y < ny || (y === ny && m <= nm)) {
    const diff = (y - sy) * 12 + (m - sm);
    if (diff % step === 0) {
      const monthStr = `${y}-${String(m + 1).padStart(2, '0')}`;
      if (!skipped.includes(monthStr)) made++;
    }
    if (m === 11) { m = 0; y++; } else { m++; }
  }
  return made;
}

function generateTenantCode() {
  const digits = Math.floor(1000 + Math.random() * 9000).toString();
  const letters = String.fromCharCode(
    65 + Math.floor(Math.random() * 26),
    65 + Math.floor(Math.random() * 26)
  );
  return digits + letters;
}

// Formatea un número de teléfono español: "622280559" → "622 280 559"
function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  return digits.match(/.{1,3}/g)?.join(' ') ?? '';
}

function saveTenantCode(code, landlordEmail, propertyId, tenantId, roomId = null) {
  const codes = JSON.parse(localStorage.getItem('tenant_codes') || '{}');
  codes[code] = { landlordEmail, propertyId, tenantId, roomId };
  localStorage.setItem('tenant_codes', JSON.stringify(codes));
}

function ExpenseMenu({ expenseId, openMenuId, setOpenMenuId, onEdit, onDelete }) {
  const isOpen = openMenuId === expenseId;
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen, setOpenMenuId]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : expenseId); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#aaa', lineHeight: 1, display: 'flex', alignItems: 'center' }}
        aria-label="Opciones del gasto"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute', right: 0, top: '100%', zIndex: 200,
            background: 'white', borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
            border: '1px solid #eee', minWidth: 120, overflow: 'hidden',
            // evita que se salga de la pantalla en móvil
            maxWidth: 'calc(100vw - 16px)',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', color: '#333' }}
          >
            Editar
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}
          >
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
}

function PropertyDetail({ property, onBack, onUpdate, landlordEmail, readOnly = false }) {
  const [expenses, setExpenses] = useState([]);
  const [showExpenses, setShowExpenses] = useState(false);
  const [expenseView, setExpenseView] = useState(() => localStorage.getItem(`expenseView_${property.id}`) || 'prorrateado'); // 'prorrateado' | 'real'
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [openMenuExpenseId, setOpenMenuExpenseId] = useState(null);
  const [showExpenseSummary, setShowExpenseSummary] = useState(false);
  const [attachmentLightbox, setAttachmentLightbox] = useState(null);
  const [tenants, setTenants] = useState(property.tenants || []);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [rooms, setRooms] = useState(property.rooms || []);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [viewingRoomId, setViewingRoomId] = useState(null); // ✅ CAMBIADO: guardamos solo el ID
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = React.useRef(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [codeModal, setCodeModal] = useState(null); // { name, code }
  const [copied, setCopied] = useState(false);
  const copyTimerRef = React.useRef(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [confirmingTenant, setConfirmingTenant] = useState(null); // { id, name, amount }
  const [chatWithTenant, setChatWithTenant] = useState(null); // { tenantId, tenantName, roomId }
  const [unreadCounts, setUnreadCounts] = useState({}); // { [tenantId]: number }
  const [showInvestmentSection, setShowInvestmentSection] = useState(false);
  const [deleteExpenseModal, setDeleteExpenseModal] = useState(null); // { expense }
  const [duplicateExpenseModal, setDuplicateExpenseModal] = useState(null); // { newExpenseData, conflict, prevStartDates }

  const handleCopyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleShareCode = (code) => {
    const shareText = `Hola, tu código de acceso para Domio es: ${code}. Entra en trydomio.com para gestionar tu alquiler.`;
    if (navigator.share) {
      navigator.share({ title: 'Código de acceso Domio', text: shareText, url: 'https://trydomio.com' }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(shareText).catch(() => {});
    }
  };

  React.useEffect(() => {
    if (!showOptionsMenu) return;
    const handleOutside = (e) => {
      if (!optionsMenuRef.current?.contains(e.target)) {
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showOptionsMenu]);

  useEffect(() => {
    if (!tenants.length || (property.status !== 'alquilado' && property.status !== 'otros')) return;
    Promise.all(
      tenants.map(t =>
        supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('landlord_email', landlordEmail)
          .eq('property_id', property.id)
          .is('room_id', null)
          .eq('tenant_id', t.id)
          .eq('sender', 'tenant')
          .eq('read_by_landlord', false)
          .then(({ count }) => [t.id, count || 0])
      )
    ).then(results => setUnreadCounts(Object.fromEntries(results)));
  }, [tenants, property.id, landlordEmail, property.status]); // eslint-disable-line


  const [paymentConfig, setPaymentConfig] = useState(property.paymentConfig || {
    startDay: 1,
    endDay: 5,
    limitDay: 5
  });
  const [payments, setPayments] = useState(property.payments || []);
  const [pendingSupabasePayments, setPendingSupabasePayments] = useState([]);

  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());

  const fetchPaymentsForMonth = React.useCallback((yr, mo) => {
    supabase
      .from('payments')
      .select('tenant_id, room_id, status, amount, confirmed_at, partial_amount, pending_amount')
      .eq('property_id', String(property.id))
      .eq('year', yr)
      .eq('month', mo)
      .then(({ data }) => { if (data) setPendingSupabasePayments(data); });
  }, [property.id]); // eslint-disable-line

  useEffect(() => {
    fetchPaymentsForMonth(currentYear, currentMonth);
  }, [currentYear, currentMonth, fetchPaymentsForMonth]);

  useEffect(() => {
    if (viewingRoomId !== null) return; // re-fetch when returning from room view, not while inside it
    supabase
      .from('expenses')
      .select('*')
      .eq('property_id', String(property.id))
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setExpenses(data); });
  }, [property.id, viewingRoomId]); // eslint-disable-line

  const [isEditMode, setIsEditMode] = useState(false);

  const minYear = 2020;
  const minMonth = 0;

  const isCurrentMonthFuture = isFutureMonth(currentYear, currentMonth);
  const isAtMinMonth = currentYear === minYear && currentMonth === minMonth;
  const isAtCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();
  const isPastMonth = !isAtCurrentMonth && !isCurrentMonthFuture;
  const canEdit = !readOnly && (isAtCurrentMonth || isEditMode);

  // ✅ NUEVO: manejador de update que también sincroniza rooms localmente
  const handleUpdate = (updatedProperty) => {
    if (updatedProperty.rooms) {
      setRooms(updatedProperty.rooms);
    }

    if (updatedProperty.payments) {
      setPayments(updatedProperty.payments);
    }
    onUpdate(updatedProperty);
  };

  const getTenantPaymentStatus = (tenantId) => {
    if (tenants.length === 0 || (property.status !== 'alquilado' && property.status !== 'otros')) return null;

    const supabasePayment = pendingSupabasePayments.find(p => p.tenant_id === tenantId);
    if (supabasePayment?.status === 'partial') return 'partial';
    if (supabasePayment?.status === 'confirmed') return 'paid';
    if (supabasePayment?.status === 'pending') return 'pending_confirmation';

    const currentPayment = payments.find(p =>
      p.year === currentYear &&
      p.month === currentMonth &&
      p.tenantId === tenantId
    );

    const today = new Date();
    const isCurrentMonthView = currentYear === today.getFullYear() && currentMonth === today.getMonth();
    
    if (!isCurrentMonthView) {
      if (currentPayment) {
        return currentPayment.status === 'confirmed' ? 'paid' : 'pending_confirmation';
      }
      return 'overdue';
    }

    const currentDay = today.getDate();
    
    if (currentPayment) {
      if (currentPayment.status === 'confirmed') return 'paid';
      if (currentPayment.status === 'pending') return 'pending_confirmation';
    }

    if (currentDay > paymentConfig.limitDay) return 'overdue';
    if (currentDay >= paymentConfig.startDay && currentDay <= paymentConfig.endDay) return 'pending';
    if (currentDay < paymentConfig.startDay) return 'not_yet';
    
    return 'pending';
  };

  const handleConfirmWithAmount = async (tenantId, importeNuevo) => {
    const tenant = tenants.find(t => t.id === tenantId);
    const rentTotal = tenant?.amount || 0;
    const existingSupabase = pendingSupabasePayments.find(p => p.tenant_id === tenantId);
    const confirmedAt = new Date().toISOString();

    // Capture previous states for rollback on error
    const prevSupabasePayments = pendingSupabasePayments;
    const prevPayments = payments;

    // Optimistic: mirror the RPC calculation for immediate UI response
    const importePrevio = existingSupabase?.partial_amount || 0;
    const nuevoTotal = importePrevio + importeNuevo;
    const isComplete = nuevoTotal >= rentTotal;
    const newStatus = isComplete ? 'confirmed' : 'partial';
    const newPartialAmount = isComplete ? null : nuevoTotal;

    setPendingSupabasePayments(prev => {
      const exists = prev.find(p => p.tenant_id === tenantId);
      const updated = {
        tenant_id: tenantId,
        room_id: null,
        status: newStatus,
        confirmed_at: confirmedAt,
        amount: rentTotal,
        partial_amount: newPartialAmount,
        pending_amount: null,
      };
      if (exists) return prev.map(p => p.tenant_id === tenantId ? { ...p, ...updated } : p);
      return [...prev, updated];
    });

    // Update local payments array (used for history and export)
    const existingLocalPayment = payments.find(p =>
      p.year === currentYear && p.month === currentMonth && p.tenantId === tenantId
    );
    let updatedPayments;
    if (existingLocalPayment) {
      updatedPayments = payments.map(p =>
        p.year === currentYear && p.month === currentMonth && p.tenantId === tenantId
          ? { ...p, status: newStatus, confirmedAt, amount: rentTotal, tenantName: tenant?.name }
          : p
      );
    } else {
      updatedPayments = [...payments, {
        year: currentYear, month: currentMonth, tenantId,
        status: newStatus, confirmedAt, amount: rentTotal, tenantName: tenant?.name,
      }];
    }
    setPayments(updatedPayments);
    onUpdate({ ...property, payments: updatedPayments });

    const { data: rpcResult, error: rpcError } = await supabase.rpc('confirm_payment_v2', {
      p_property_id:      String(property.id),
      p_room_id:          null,
      p_tenant_id:        tenantId,
      p_year:             currentYear,
      p_month:            currentMonth,
      p_confirmed_amount: importeNuevo,
      p_rent_total:       rentTotal,
      p_landlord_email:   property.landlord_email || landlordEmail,
    });

    if (rpcError || rpcResult?.success === false) {
      // Rollback optimistic state
      setPendingSupabasePayments(prevSupabasePayments);
      setPayments(prevPayments);
      onUpdate({ ...property, payments: prevPayments });
      alert('Error al guardar el pago. Por favor, recarga la página.');
      return;
    }

    if (rpcResult) {
      // Correct optimistic state with authoritative RPC result
      setPendingSupabasePayments(prev =>
        prev.map(p => p.tenant_id === tenantId
          ? {
              ...p,
              status: rpcResult.status,
              partial_amount: rpcResult.partial_amount,
              amount: rpcResult.amount,
              pending_amount: null,
            }
          : p
        )
      );
    }

    setConfirmingTenant(null);
  };

  // Mark several past months as fully paid at once (from Historial de pagos)
  const handleBulkConfirm = async (tenantId, months, existingRows) => {
    const tenant = tenants.find(t => t.id === tenantId);
    const rentTotal = tenant?.amount || 0;
    const confirmedAt = new Date().toISOString();
    const results = await Promise.all(months.map(async ({ year, month }) => {
      const existing = existingRows.find(r => r.tenant_id === tenantId && r.year === year && r.month === month);
      const remaining = rentTotal - (existing?.partial_amount || 0);
      const { data, error } = await supabase.rpc('confirm_payment_v2', {
        p_property_id:      String(property.id),
        p_room_id:          null,
        p_tenant_id:        tenantId,
        p_year:             year,
        p_month:            month,
        p_confirmed_amount: remaining,
        p_rent_total:       rentTotal,
        p_landlord_email:   property.landlord_email || landlordEmail,
      });
      return { year, month, ok: !error && data?.success !== false };
    }));

    const saved = results.filter(r => r.ok);
    if (saved.length > 0) {
      let updatedPayments = [...payments];
      saved.forEach(({ year, month }) => {
        const idx = updatedPayments.findIndex(p => p.year === year && p.month === month && p.tenantId === tenantId);
        const entry = { year, month, tenantId, status: 'confirmed', confirmedAt, amount: rentTotal, tenantName: tenant?.name };
        if (idx >= 0) updatedPayments[idx] = { ...updatedPayments[idx], ...entry };
        else updatedPayments.push(entry);
      });
      setPayments(updatedPayments);
      onUpdate({ ...property, payments: updatedPayments });
      fetchPaymentsForMonth(currentYear, currentMonth);
    }
    return { saved: saved.length, failed: results.length - saved.length };
  };

  const handleRejectPayment = async (tenantId) => {
    const updatedPayments = payments.filter(p =>
      !(p.year === currentYear && p.month === currentMonth && p.tenantId === tenantId)
    );
    setPayments(updatedPayments);
    onUpdate({ ...property, payments: updatedPayments });
    await supabase
      .from('payments')
      .delete()
      .eq('property_id', String(property.id))
      .eq('tenant_id', tenantId)
      .eq('year', currentYear)
      .eq('month', currentMonth);
    setPendingSupabasePayments(prev => prev.filter(p => p.tenant_id !== tenantId));
  };

  const handleCancelPayment = (tenantId) => {
    if (window.confirm('¿Estás seguro de que quieres cancelar este pago confirmado?')) {
      handleRejectPayment(tenantId);
    }
  };


  const getPaymentStatusInfo = (status) => {
    const statusMap = {
      'paid': { label: 'Pagado', color: 'green', icon: '✓' },
      'partial': { label: 'Pago parcial', color: 'yellow', icon: '◑' },
      'pending_confirmation': { label: 'Por confirmar', color: 'orange', icon: '⏱' },
      'pending': { label: 'Pendiente', color: 'gray', icon: '○' },
      'overdue': { label: 'Retrasado', color: 'red', icon: '!' },
      'not_yet': { label: 'Pendiente', color: 'gray', icon: '○' }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const goToPrevMonth = () => {
    if (isAtMinMonth) return;
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else { setCurrentMonth(m => m - 1); }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else { setCurrentMonth(m => m + 1); }
  };

  useEffect(() => {
    setIsEditMode(false);
  }, [currentYear, currentMonth]);

  const visibleExpenses = (expenseView === 'real'
    ? getExpensesForMonthReal(expenses, currentYear, currentMonth)
    : getExpensesForMonth(expenses, currentYear, currentMonth)
  ).filter(e => e.active !== false);

  const ownershipPct = property.ownershipPercentage || 100;
  const totalExpenses = visibleExpenses.reduce((sum, exp) => {
    if (exp.active === false) return sum;
    if (expenseView !== 'real' && isExpensePendingVariable(exp, currentYear, currentMonth)) return sum;
    return sum + getAmountForView(exp, expenseView);
  }, 0);
  const myExpenses = visibleExpenses.reduce((sum, exp) => {
    if (exp.active === false) return sum;
    if (expenseView !== 'real' && isExpensePendingVariable(exp, currentYear, currentMonth)) return sum;
    const pct = exp.expense_percentage != null ? exp.expense_percentage : ownershipPct;
    return sum + getAmountForView(exp, expenseView) * pct / 100;
  }, 0);
  
  const ownershipPercentage = property.ownershipPercentage || 100;
  const ownershipMultiplier = ownershipPercentage / 100;
  
  let monthlyIncome = 0;
  
  if (property.status === 'por_habitaciones') {
    // Suma todos los pagos confirmados de todas las habitaciones (pueden ser múltiples por habitación)
    monthlyIncome = rooms.reduce((sum, room) => {
      const supabaseRoomPmt = pendingSupabasePayments.find(p => p.room_id === room.id);
      if (supabaseRoomPmt?.status === 'partial') {
        return sum + (supabaseRoomPmt.partial_amount || 0);
      }
      const roomConfirmed = payments.filter(p =>
        p.year === currentYear &&
        p.month === currentMonth &&
        p.roomId === room.id &&
        p.status === 'confirmed'
      );
      return sum + roomConfirmed.reduce((s, p) => s + (p.amount ?? room.price), 0);
    }, 0) * ownershipMultiplier;
  } else {
    // Suma pagos de inquilinos activos
    const activeTotal = tenants.reduce((sum, tenant) => {
      const supabasePmt = pendingSupabasePayments.find(p => p.tenant_id === tenant.id);
      if (supabasePmt?.status === 'partial') {
        return sum + (supabasePmt.partial_amount || 0);
      }
      const tenantConfirmed = payments.filter(p =>
        p.year === currentYear &&
        p.month === currentMonth &&
        p.tenantId === tenant.id &&
        p.status === 'confirmed'
      );
      return sum + tenantConfirmed.reduce((s, p) => s + (p.amount ?? tenant.amount), 0);
    }, 0);
    // Suma pagos de ex-inquilinos
    const orphanTotal = payments
      .filter(p => p.year === currentYear && p.month === currentMonth && p.status === 'confirmed' && p.tenantName && !tenants.find(t => t.id === p.tenantId))
      .reduce((sum, p) => sum + (p.amount ?? 0), 0);
    monthlyIncome = (activeTotal + orphanTotal) * ownershipMultiplier;
  }
  
  const netIncome = isCurrentMonthFuture ? null : monthlyIncome - myExpenses;

  // ─── Historial ───
  // First month with data: property creation, payments, expenses, tenant start
  const historyStart = earliestMonth([
    property.createdAt,
    ...payments.map(p => new Date(p.year, p.month, 1)),
    ...expenses.map(e => e.start_date ? e.start_date.substring(0, 10) + 'T12:00:00' : null),
    ...tenants.map(getTenantStartIndex).filter(i => i != null).map(i => new Date(Math.floor(i / 12), i % 12, 1)),
    ...rooms.map(r => getTenantStartIndex(r.tenant)).filter(i => i != null).map(i => new Date(Math.floor(i / 12), i % 12, 1)),
  ]);
  const nowIdx = monthIndex(now.getFullYear(), now.getMonth());

  // Tenants have no contract dates: a tenant counts as "with contract" in a month
  // if there is a payment record for it that month, or if it is a current tenant
  // who was already added by then (unknown start → only from the current month).
  const currentTenantActive = (tenant, idx) => {
    const startIdx = getTenantStartIndex(tenant);
    return (startIdx != null ? startIdx : nowIdx) <= idx;
  };

  const isRoomOccupied = (room, y, m) => {
    if (payments.some(p => p.year === y && p.month === m && p.roomId === room.id)) return true;
    return !!room.tenant && currentTenantActive(room.tenant, monthIndex(y, m));
  };

  const isPropertyRented = (y, m) => {
    if (payments.some(p => p.year === y && p.month === m && !p.roomId && (p.tenantId || p.tenantName))) return true;
    if (property.status !== 'alquilado' && property.status !== 'otros') return false;
    return tenants.some(t => currentTenantActive(t, monthIndex(y, m)));
  };

  const computeHistoryMonth = (y, m) => {
    const ownershipPercentage = property.ownershipPercentage || 100;
    const ownershipMultiplier = ownershipPercentage / 100;

    const monthExpenses = expenseView === 'real'
      ? getExpensesForMonthReal(expenses, y, m)
      : getExpensesForMonth(expenses, y, m);
    const myExpenses = monthExpenses.reduce((sum, e) => {
      if (e.active === false) return sum;
      if (expenseView !== 'real' && isExpensePendingVariable(e, y, m)) return sum;
      const pct = e.expense_percentage != null ? e.expense_percentage : ownershipPercentage;
      return sum + getAmountForView(e, expenseView) * pct / 100;
    }, 0);

    let allPaymentsTotal = 0;
    let status;

    if (property.status === 'por_habitaciones') {
      // Solo pagos por roomId para evitar doble conteo
      allPaymentsTotal = rooms.reduce((sum, room) => {
        const rp = payments.filter(p => p.year === y && p.month === m && p.roomId === room.id && p.status === 'confirmed');
        return sum + rp.reduce((s, p) => s + (p.amount ?? room.price), 0);
      }, 0);
      const occupied = rooms.filter(r => isRoomOccupied(r, y, m)).length;
      status = occupied > 0
        ? { label: `${occupied}/${rooms.length} hab.`, active: true }
        : { label: 'Vacío', active: false };
    } else {
      // Pagos de inquilinos activos
      const confirmedPaymentsTotal = tenants.reduce((sum, tenant) => {
        const tenantPayments = payments.filter(p =>
          p.year === y && p.month === m && p.tenantId === tenant.id && p.status === 'confirmed'
        );
        return sum + tenantPayments.reduce((s, p) => s + (p.amount ?? tenant.amount), 0);
      }, 0);
      // Pagos de ex-inquilinos
      const orphanPaymentsTotal = payments
        .filter(p => p.year === y && p.month === m && p.status === 'confirmed' && p.tenantName && !tenants.find(t => t.id === p.tenantId))
        .reduce((sum, p) => sum + (p.amount ?? 0), 0);
      allPaymentsTotal = confirmedPaymentsTotal + orphanPaymentsTotal;
      status = isPropertyRented(y, m)
        ? { label: 'Alquilado', active: true }
        : { label: 'Vacío', active: false };
    }
    const income = allPaymentsTotal > 0 ? allPaymentsTotal * ownershipMultiplier : 0;
    return { income, expenses: myExpenses, status };
  };

  const doInsertExpense = async (expenseData, prevStartDates) => {
    const base = { property_id: String(property.id), landlord_email: landlordEmail, ...expenseData };
    const rows = (prevStartDates && prevStartDates.length > 0)
      ? [base, ...prevStartDates.map(sd => ({ ...base, start_date: sd }))]
      : [base];
    const { data, error } = await supabase.from('expenses').insert(rows).select();
    if (error) { alert(`Error guardando el gasto: ${error.message}`); return; }
    setExpenses(prev => [...(data || []), ...prev]);
    setShowAddExpense(false);
  };

  const handleAddExpense = async (expenseData, prevStartDates) => {
    // Check for duplicate recurring expenses: the new expense and an existing one
    // both active in the currently viewed month, same category + frequency
    if (expenseData.frequency !== 'unico') {
      const newExpenseActive = getExpensesForMonth([{ ...expenseData, active: true }], currentYear, currentMonth).length > 0;
      if (newExpenseActive) {
        const conflict = expenses.find(e => {
          if (e.active === false) return false;
          if (e.frequency === 'unico' || e.type === 'puntual') return false;
          if (e.category !== expenseData.category) return false;
          if (e.frequency !== expenseData.frequency) return false;
          return getExpensesForMonth([e], currentYear, currentMonth).length > 0;
        });
        if (conflict) {
          const newStart = new Date(expenseData.start_date.substring(0, 10) + 'T12:00:00');
          const existStart = new Date((conflict.start_date || conflict.created_at || '').substring(0, 10) + 'T12:00:00');
          setDuplicateExpenseModal({ newExpenseData: expenseData, conflict, prevStartDates, canExtend: existStart > newStart });
          return;
        }
      }
    }
    await doInsertExpense(expenseData, prevStartDates);
  };

  const handleExtendExistingExpense = async () => {
    const { newExpenseData, conflict } = duplicateExpenseModal;
    const { error } = await supabase
      .from('expenses')
      .update({ start_date: newExpenseData.start_date })
      .eq('id', conflict.id);
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === conflict.id ? { ...e, start_date: newExpenseData.start_date } : e));
    setDuplicateExpenseModal(null);
    setShowAddExpense(false);
  };

  const handleDeleteExpense = async (expense) => {
    const isRecurring = expense.type === 'recurrente_fijo' || expense.type === 'recurrente_temporal';
    if (isRecurring) {
      setDeleteExpenseModal({ expense });
    } else {
      const { error } = await supabase.from('expenses').delete().eq('id', expense.id);
      if (error) { alert(`Error al eliminar el gasto: ${error.message}`); return; }
      setExpenses(prev => prev.filter(e => e.id !== expense.id));
    }
  };

  const handleSkipExpenseMonth = async (expenseId) => {
    const expense = expenses.find(e => e.id === expenseId);
    const step = expense?.frequency === 'trimestral' ? 3
      : expense?.frequency === 'anual' ? 12
      : expense?.frequency === 'custom' ? (expense?.custom_frequency_months || 1)
      : 1;
    const monthsToSkip = [];
    let y = currentYear, m = currentMonth;
    for (let i = 0; i < step; i++) {
      monthsToSkip.push(`${y}-${String(m + 1).padStart(2, '0')}`);
      if (m === 11) { m = 0; y++; } else { m++; }
    }
    const currentSkipped = expense?.skipped_months || [];
    const updatedSkipped = [...new Set([...currentSkipped, ...monthsToSkip])];
    await supabase.from('expenses').update({ skipped_months: updatedSkipped }).eq('id', expenseId);
    setExpenses(prev => prev.map(e => e.id === expenseId ? { ...e, skipped_months: updatedSkipped } : e));
    setDeleteExpenseModal(null);
  };

  const handleDeleteExpensePermanently = async (expenseId) => {
    await supabase.from('expenses').delete().eq('id', expenseId);
    setExpenses(prev => prev.filter(e => e.id !== expenseId));
    setDeleteExpenseModal(null);
  };

  const handleUpdateExpense = async (expenseId, expenseData) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(expenseData)
      .eq('id', expenseId)
      .select()
      .single();
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === expenseId ? data : e));
    setEditingExpense(null);
  };

  const handleUpdateVariableAmount = async (expenseId, newAmount) => {
    const expense = expenses.find(e => e.id === expenseId);
    const periodStart = getCurrentPeriodStart(expense, currentYear, currentMonth);
    await supabase.from('expenses').update({
      amount: parseFloat(newAmount),
      period_start: periodStart,
    }).eq('id', expenseId);
    setExpenses(prev => prev.map(e =>
      e.id === expenseId
        ? { ...e, amount: parseFloat(newAmount), period_start: periodStart }
        : e
    ));
  };

  const handleAddTenant = async (tenantData) => {
    const code = generateTenantCode();
    const newTenant = {
      ...tenantData,
      id: Date.now().toString(),
      amount: tenantData.isShared === false ? property.price : tenantData.amount,
      tenantCode: code
    };
    const updatedTenants = [...tenants, newTenant];

    if (landlordEmail) {
      saveTenantCode(code, landlordEmail, property.id, newTenant.id);
    }

    // Guardar en Supabase
    if (landlordEmail) {
      const { data: insertedTenant, error: supabaseError } = await supabase.from('inquilinos').insert({
        email: tenantData.email ? tenantData.email.toLowerCase().trim() : null,
        landlord_email: landlordEmail,
        property_id: property.id,
        tenant_id: newTenant.id,
        tenant_code: code,
        tenant_name: tenantData.name,
        property_name: property.name,
        monthly_rent: newTenant.amount || property.price,
        payment_config: property.paymentConfig || { startDay: 1, endDay: 5 },
        room_id: null,
      });
      console.log('INSERT INQUILINO:', insertedTenant, supabaseError);
      if (supabaseError) {
        console.error('Error al guardar inquilino en Supabase:', supabaseError);
      } else {
        console.log('Inquilino guardado en Supabase correctamente:', code);
      }
    } else {
      console.warn('No se guardó en Supabase: landlordEmail no disponible');
    }

    const propertyUpdate = {
      ...property,
      tenants: updatedTenants,
      isSharedProperty: tenantData.isShared !== undefined ? tenantData.isShared : property.isSharedProperty
    };

    setTenants(updatedTenants);
    onUpdate(propertyUpdate);
    setShowAddTenant(false);
  };

  const handleEditTenant = (tenantData) => {
    const updatedTenants = tenants.map(t => 
      t.id === editingTenant.id ? { ...tenantData, id: t.id } : t
    );
    setTenants(updatedTenants);
    onUpdate({ ...property, tenants: updatedTenants });
    setEditingTenant(null);
  };

  const handleDeleteTenant = (tenantId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este inquilino?')) {
      const tenant = tenants.find(t => t.id === tenantId);
      // Conservar nombre en pagos existentes
      const updatedPayments = payments.map(p =>
        p.tenantId === tenantId && !p.tenantName
          ? { ...p, tenantName: tenant?.name }
          : p
      );
      const updatedTenants = tenants.filter(t => t.id !== tenantId);
      setTenants(updatedTenants);
      setPayments(updatedPayments);
      onUpdate({ ...property, tenants: updatedTenants, payments: updatedPayments });
      setEditingTenant(null);
    }
  };

  const handleAddRoom = (roomData) => {
    const newRoom = {
      ...roomData,
      id: Date.now().toString(),
      status: 'vacia',
      tenant: null
    };
    const updatedRooms = [...rooms, newRoom];
    const updatedPrice = updatedRooms.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0);
    setRooms(updatedRooms);
    onUpdate({ ...property, rooms: updatedRooms, price: updatedPrice });
    setShowAddRoom(false);
  };

  const handleEditRoom = (updatedRoom) => {
    const updatedRooms = rooms.map(r => r.id === updatedRoom.id ? updatedRoom : r);
    const updatedPrice = updatedRooms.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0);
    setRooms(updatedRooms);
    onUpdate({ ...property, rooms: updatedRooms, price: updatedPrice });
    setEditingRoom(null);
  };

  const handleDeleteRoom = (roomId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta habitación?')) {
      const updatedRooms = rooms.filter(r => r.id !== roomId);
      const updatedPrice = updatedRooms.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0);
      setRooms(updatedRooms);
      onUpdate({ ...property, rooms: updatedRooms, price: updatedPrice });
      setEditingRoom(null);
    }
  };

  const handleAssignTenantToRoom = (roomId, tenantData) => {
    const updatedRooms = rooms.map(r => 
      r.id === roomId ? { ...r, tenant: tenantData, status: 'ocupada' } : r
    );
    setRooms(updatedRooms);
    onUpdate({ ...property, rooms: updatedRooms });
  };

  const handleRemoveTenantFromRoom = (roomId) => {
    const updatedRooms = rooms.map(r => 
      r.id === roomId ? { ...r, tenant: null, status: 'vacia' } : r
    );
    setRooms(updatedRooms);
    onUpdate({ ...property, rooms: updatedRooms });
  };

  if (showDocuments) {
    return <PropertyDocuments property={property} landlordEmail={landlordEmail} onBack={() => setShowDocuments(false)} onUpdate={onUpdate} />;
  }

  if (showExpenseSummary) {
    const getMonthlyIncome = (yr, mo) =>
      payments
        .filter(p => p.year === yr && p.month === mo && p.status === 'confirmed')
        .reduce((s, p) => s + (p.amount ?? 0), 0) * ((property.ownershipPercentage || 100) / 100);
    return (
      <ExpenseSummary
        expenses={expenses}
        onBack={() => setShowExpenseSummary(false)}
        propertyName={property.name}
        getMonthlyIncome={getMonthlyIncome}
      />
    );
  }

  if (showPaymentHistory) {
    return (
      <PaymentHistory
        propertyId={property.id}
        payments={payments}
        tenants={tenants}
        onBulkConfirm={handleBulkConfirm}
        onSyncPayments={(updated) => { setPayments(updated); onUpdate({ ...property, payments: updated }); }}
        onBack={() => setShowPaymentHistory(false)}
      />
    );
  }

  if (chatWithTenant) {
    return (
      <ChatConversation
        landlordEmail={landlordEmail}
        propertyId={property.id}
        roomId={chatWithTenant.roomId}
        tenantId={chatWithTenant.tenantId}
        tenantName={chatWithTenant.tenantName}
        propertyName={property.name}
        currentRole="landlord"
        onBack={() => setChatWithTenant(null)}
      />
    );
  }

  // ✅ CORREGIDO: buscar la habitación actualizada por ID en el array rooms local
  if (viewingRoomId) {
    const currentRoom = rooms.find(r => r.id === viewingRoomId);
    if (currentRoom) {
      return (
        <RoomDetail
          room={currentRoom}
          property={{ ...property, rooms, payments }}
          onBack={() => setViewingRoomId(null)}
          onUpdate={handleUpdate}
          landlordEmail={landlordEmail}
        />
      );
    }
  }

  const handleExpenseAdded = () => {
    supabase
      .from('expenses')
      .select('*')
      .eq('property_id', String(property.id))
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setExpenses(data); });
  };

  return (
    <div className="property-detail-container">

      {/* Header */}
      <div className="detail-header" ref={optionsMenuRef}>
        <button className="back-button" onClick={onBack}>←</button>
        <h1 className="detail-title">{property.name}</h1>
        {!readOnly && <button className="detail-options" onClick={() => setShowOptionsMenu(!showOptionsMenu)}>⋮</button>}
        {!readOnly && showOptionsMenu && (
          <div className="detail-options-menu">
            <button onClick={() => { setShowEditModal(true); setShowOptionsMenu(false); }} className="detail-option-item">Editar propiedad</button>
            {tenants.map(t => (
              <button key={t.id} className="detail-option-item" onClick={() => {
                let code = t.tenantCode;
                if (!code) {
                  code = generateTenantCode();
                  const updatedTenants = tenants.map(x => x.id === t.id ? { ...x, tenantCode: code } : x);
                  setTenants(updatedTenants);
                  onUpdate({ ...property, tenants: updatedTenants });
                  if (landlordEmail) saveTenantCode(code, landlordEmail, property.id, t.id);
                }
                setCodeModal({ name: t.name, code });
                setCopied(false);
                setShowOptionsMenu(false);
              }}>
                Código{tenants.length > 1 ? ` de ${t.name.split(' ')[0]}` : ' del inquilino'}
              </button>
            ))}
            <button onClick={() => {
              if (window.confirm('¿Estás seguro de que quieres eliminar esta propiedad?')) {
                onUpdate({ ...property, deleted: true }); onBack();
              }
              setShowOptionsMenu(false);
            }} className="detail-option-item delete">Eliminar propiedad</button>
          </div>
        )}
        {codeModal && (
          <div className="modal-overlay" onClick={() => { setCodeModal(null); setCopied(false); clearTimeout(copyTimerRef.current); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
              <div className="modal-header">
                <h2>Código del inquilino</h2>
                <button className="modal-close" onClick={() => { setCodeModal(null); setCopied(false); clearTimeout(copyTimerRef.current); }}>×</button>
              </div>
              <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px' }}>Comparte este código con <strong>{codeModal.name}</strong></p>
              <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '8px', margin: 0, color: '#111' }}>{codeModal.code}</p>
              </div>
              <button className="submit-button" onClick={() => handleCopyCode(codeModal.code)}>
                {copied ? 'Copiado' : 'Copiar código'}
              </button>
              <button
                onClick={() => handleShareCode(codeModal.code)}
                style={{
                  marginTop: 10, width: '100%', padding: '12px', borderRadius: '12px',
                  background: 'white', border: '1px solid #111', cursor: 'pointer',
                  fontSize: '15px', fontWeight: '600', color: '#111',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="16 6 12 2 8 6" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="2" x2="12" y2="15" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Compartir
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Month Navigator */}
      <MonthNavigator
        year={currentYear}
        month={currentMonth}
        minYear={minYear}
        minMonth={minMonth}
        onPrev={goToPrevMonth}
        onNext={goToNextMonth}
        onJump={(yr, mo) => { setCurrentYear(yr); setCurrentMonth(mo); }}
        isPastMonth={isPastMonth}
        isEditMode={isEditMode}
        onEdit={readOnly ? undefined : () => setIsEditMode(true)}
        onSave={() => setIsEditMode(false)}
        onCancel={() => { setIsEditMode(false); fetchPaymentsForMonth(currentYear, currentMonth); }}
      />

      {/* Gauge */}
      <div className="profitability-section">
        <div className="gauge-chart">
          <svg width="360" height="220" viewBox="0 0 360 220">
            <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#E0E0E0" strokeWidth="35" strokeLinecap="round"/>
            {!isCurrentMonthFuture && netIncome !== null && (
              netIncome >= 0 ? (
                <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#4CAF50" strokeWidth="35" strokeLinecap="round"
                  strokeDasharray="408" strokeDashoffset={408 - (Math.min(netIncome / property.price, 1) * 408)}/>
              ) : (
                <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#F44336" strokeWidth="35" strokeLinecap="round"/>
              )
            )}
          </svg>
          <div className="gauge-center">
            {isCurrentMonthFuture ? (
              <><div className="net-income future">—</div><div className="income-sublabel">Mes futuro</div></>
            ) : (
              <>
                <div className="net-income">{netIncome >= 0 ? '+' : ''}{netIncome.toFixed(2)} €</div>
                <div className="income-label">/mes</div>
                <div className="income-sublabel" style={{ color: netIncome < 0 && monthlyIncome === 0 && property.status !== 'uso_propio' ? '#F44336' : '#999' }}>
                  {netIncome < 0 && monthlyIncome === 0 && property.status !== 'uso_propio' ? 'Pago sin confirmar' : 'Neto'}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="profitability-label">{isCurrentMonthFuture ? 'Sin datos todavía' : 'Rentabilidad mensual estimada'}</p>
        {!isCurrentMonthFuture && (
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#aaa', textAlign: 'center' }}>
            {expenseView === 'real' ? 'Gastos reales' : 'Gastos prorrateados'}
          </p>
        )}
      </div>

      {/* Estado */}
      <div className="info-card status-card" onClick={() => setShowDocuments(true)} style={{ cursor: 'pointer' }}>
        <div className="card-header">
          <div>
            <h3>Estado</h3>
            <div className="status-info">
              <span className={`status-dot ${property.status}`}></span>
              <span>
                {property.status === 'alquilado' ? 'Alquilado' :
                 property.status === 'por_habitaciones' ? 'Por habitaciones' :
                 property.status === 'otros' ? (tenants.length > 0 ? 'Alquilado' : 'Vacío') : 'Vacío'}
              </span>
            </div>
            {property.status === 'otros' && property.customType && (
              <p className="contract-date">{property.customType}</p>
            )}
            {property.status === 'alquilado' && property.contractEnd && (
              <p className="contract-date">Contrato hasta {property.contractEnd}</p>
            )}
            {property.status === 'por_habitaciones' && rooms.length > 0 && (
              <p className="contract-date">{rooms.filter(r => r.status === 'ocupada').length}/{rooms.length} ocupadas</p>
            )}
          </div>
          <button className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Habitaciones */}
      {property.status === 'por_habitaciones' && (
        <div className="info-card rooms-card">
          <div className="card-header">
            <h3>Habitaciones</h3>
            {canEdit && (
              <button className="add-tenant-button" onClick={() => setShowAddRoom(true)}>
                + Añadir habitación
              </button>
            )}
          </div>
          
          {rooms.length > 0 && (
            <div className="rooms-grid">
              {rooms.map(room => {
                const roomPayment = payments.find(p =>
                  p.year === currentYear && p.month === currentMonth && p.roomId === room.id
                );
                const paymentDot = roomPayment?.status === 'confirmed' ? 'green'
                  : roomPayment?.status === 'pending' ? 'orange'
                  : room.tenant ? 'gray' : null;

                return (
                  <div key={room.id} className="room-card" onClick={() => setViewingRoomId(room.id)}>
                    <div className="room-header">
                      <h4 className="room-name">{room.name}</h4>
                      {room.tenant && paymentDot && (
                        <span style={{
                          width: '26px', height: '26px', borderRadius: '50%', display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          background: paymentDot === 'green' ? '#E8F5E9' : '#F5F5F5',
                        }}>
                          {paymentDot === 'green' ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <path d="M20 6L9 17l-5-5" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="9" stroke={paymentDot === 'orange' ? '#FF9800' : '#9E9E9E'} strokeWidth="2.5"/>
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                    <p className="room-price">{room.price}€/mes</p>
                    <div className="room-tenant" style={{ borderTop: 'none', paddingTop: 0, marginTop: '10px' }}>
                      <span style={{
                        width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                        background: room.tenant ? '#4CAF50' : '#F44336', display: 'inline-block'
                      }}></span>
                      {room.tenant
                        ? <span className="room-tenant-name">{room.tenant.name}</span>
                        : <span style={{ fontSize: '12px', color: '#aaa', margin: 0 }}>Vacía</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pago del alquiler */}
      {(property.status === 'alquilado' || property.status === 'otros') && (tenants.length > 0 || payments.some(p => p.year === currentYear && p.month === currentMonth && p.status === 'confirmed')) && (
        <div className="info-card payment-card">
          <div className="card-header">
            <h3>Pago del alquiler</h3>
          </div>
          
          <div className="payment-content">
            <div className="tenants-payment-grid">
              {tenants.map(tenant => {
                const tenantStatus = getTenantPaymentStatus(tenant.id);
                const supabasePaymentForTenant = pendingSupabasePayments.find(p => p.tenant_id === tenant.id);
                const localPayment = payments.find(p =>
                  p.year === currentYear && p.month === currentMonth && p.tenantId === tenant.id
                );
                // Supabase es fuente de verdad; localStorage como fallback solo para datos históricos
                const tenantPayment = supabasePaymentForTenant
                  ? {
                      ...localPayment,
                      amount: supabasePaymentForTenant.amount ?? localPayment?.amount,
                      status: supabasePaymentForTenant.status,
                      confirmedAt: supabasePaymentForTenant.confirmed_at ?? localPayment?.confirmedAt,
                    }
                  : localPayment;
                const statusInfo = getPaymentStatusInfo(tenantStatus);
                const partialAmount = supabasePaymentForTenant?.partial_amount ?? 0;

                return (
                  <div key={tenant.id} className="tenant-payment-card">
                    <div className="tenant-payment-header">
                      <div className="tenant-avatar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="7" r="4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="tenant-payment-info">
                        {tenants.length > 1 && <p className="tenant-payment-name">{tenant.name}</p>}
                        <p className="tenant-payment-amount" style={tenants.length === 1 ? { margin: 0, fontSize: '15px', fontWeight: 600, color: '#111' } : {}}>{tenant.amount}€</p>
                      </div>
                      <span className={`payment-status-badge ${statusInfo.color} small`}>
                        {tenantStatus === 'partial' ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2a10 10 0 0 1 0 20V2z" fill="#F59E0B"/>
                            <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none"/>
                          </svg>
                        ) : statusInfo.icon}
                      </span>
                    </div>

                    {canEdit && tenantStatus === 'pending_confirmation' && (
                      <div className="tenant-payment-actions">
                        {supabasePaymentForTenant?.pending_amount != null && (
                          <p style={{ fontSize: '13px', color: '#888', margin: '0 0 8px 0' }}>
                            {supabasePaymentForTenant.pending_amount}€ enviados por el inquilino
                          </p>
                        )}
                        <button className="payment-btn confirm small" onClick={() => setConfirmingTenant({ id: tenant.id, name: tenant.name, amount: supabasePaymentForTenant?.pending_amount ?? tenant.amount })}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Confirmar
                        </button>
                        <button className="payment-btn reject small" onClick={() => handleRejectPayment(tenant.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Rechazar
                        </button>
                      </div>
                    )}

                    {tenantStatus === 'paid' && (
                      <div className="tenant-payment-actions">
                        <p className="payment-confirmed-text">
                          ✓ {tenantPayment?.amount !== undefined ? `${tenantPayment.amount} €` : `${tenant.amount} €`}
                          {tenantPayment?.amount !== undefined && tenantPayment.amount !== tenant.amount && (
                            <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '4px' }}>(base: {tenant.amount} €)</span>
                          )}
                          {tenantPayment?.confirmedAt && ' · '}{tenantPayment?.confirmedAt && new Date(tenantPayment.confirmedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </p>
                        {canEdit && (
                          <button className="payment-cancel-link" onClick={() => handleCancelPayment(tenant.id)}>
                            Cancelar
                          </button>
                        )}
                      </div>
                    )}

                    {tenantStatus === 'partial' && (
                      <div className="tenant-payment-actions">
                        <p className="payment-confirmed-text" style={{ color: '#92400E' }}>
                          {partialAmount}€ de {tenant.amount}€ — pago parcial
                        </p>
                        {canEdit && (
                          <button
                            className="payment-btn confirm small"
                            onClick={() => setConfirmingTenant({
                              id: tenant.id,
                              name: tenant.name,
                              amount: tenant.amount - partialAmount,
                            })}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Confirmar resto
                          </button>
                        )}
                      </div>
                    )}

                    {canEdit && (tenantStatus === 'pending' || tenantStatus === 'overdue' || tenantStatus === 'not_yet') && (
                      <div className="tenant-payment-actions">
                        <button className="payment-btn confirm small full-width" onClick={() => setConfirmingTenant({ id: tenant.id, name: tenant.name, amount: tenant.amount })}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Confirmar pago
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagos de ex-inquilinos que ya no están */}
            {payments
              .filter(p => p.year === currentYear && p.month === currentMonth && p.status === 'confirmed' && p.tenantName && !tenants.find(t => t.id === p.tenantId))
              .map(p => (
                <div key={p.tenantId || p.tenantName} className="tenant-payment-card" style={{ background: '#f9f9f9', marginTop: '8px' }}>
                  <div className="tenant-payment-header">
                    <div className="tenant-avatar">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="tenant-payment-info">
                      <p className="tenant-payment-name" style={{ color: '#888' }}>{p.tenantName}</p>
                      <p className="tenant-payment-amount">{p.amount} €</p>
                    </div>
                    <span className="payment-status-badge green small">✓</span>
                  </div>
                  <div className="tenant-payment-actions">
                    <p className="payment-confirmed-text">
                      ✓ {new Date(p.confirmedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </p>
                    <button className="payment-cancel-link" onClick={() => {
                      if (window.confirm('¿Eliminar este pago del historial?')) {
                        const updated = payments.filter(x => !(x.year === p.year && x.month === p.month && x.tenantId === p.tenantId && x.tenantName === p.tenantName));
                        setPayments(updated);
                        onUpdate({ ...property, payments: updated });
                      }
                    }}>Eliminar</button>
                  </div>
                </div>
              ))
            }

            <div className="payment-info-single" style={{ padding: '8px 12px', background: 'none', border: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontSize: '12px', color: '#aaa' }}>
                  Rango de pago: {paymentConfig.startDay}–{paymentConfig.endDay} {new Date(currentYear, currentMonth).toLocaleDateString('es-ES', { month: 'short' })}
                </span>
              </div>
            </div>

            <button className="payment-history-btn" onClick={() => setShowPaymentHistory(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <polyline points="23 4 23 10 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Historial de pagos
            </button>
          </div>
        </div>
      )}

      {/* Gastos */}
      <div className="info-card expenses-card">
        <div className="card-header clickable" onClick={() => setShowExpenses(!showExpenses)} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>Gastos</h3>
            <span className="expenses-total" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{totalExpenses.toFixed(2)} {expenseView === 'real' ? '€' : '€/mes'}</span>
              <span className="arrow">{showExpenses ? '▼' : '›'}</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setShowExpenseSummary(true)}
              style={{ fontSize: 11, padding: '3px 9px', borderRadius: 8, border: '1px solid #ddd', background: 'white', color: '#666', cursor: 'pointer' }}>
              Resumen
            </button>
            <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 6, padding: 2, gap: 1 }}>
              {['prorrateado', 'real'].map(v => (
                <button key={v} onClick={() => { localStorage.setItem(`expenseView_${property.id}`, v); setExpenseView(v); }} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: 'none', background: expenseView === v ? 'white' : 'transparent', color: expenseView === v ? '#333' : '#999', cursor: 'pointer', fontWeight: expenseView === v ? 600 : 400 }}>
                  {v === 'prorrateado' ? 'Prorrat.' : 'Real'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {showExpenses && (
          <div className="expenses-detail">
            {visibleExpenses.length === 0 ? (
              <p className="no-expenses">{isCurrentMonthFuture ? 'No hay datos para meses futuros' : 'No hay gastos añadidos'}</p>
            ) : (
              visibleExpenses.map(expense => {
                // variable con frecuencia programada y sin importe → pendiente
                const isPendingVariable = expenseView !== 'real' && isExpensePendingVariable(expense, currentYear, currentMonth);
                const monthly = isPendingVariable ? 0 : getAmountForView(expense, expenseView);
                const isPaying = expenseView === 'real' || isPaymentMonth(expense, currentYear, currentMonth);
                const showPendingInput = isPendingVariable && isPaymentMonth(expense, currentYear, currentMonth);
                const expPct = expense.expense_percentage != null ? expense.expense_percentage : ownershipPct;
                const freqLabel = { trimestral: 'Trimestral', anual: 'Anual', unico: 'Único', manual: 'Manual', custom: `Cada ${expense.custom_frequency_months}m`, mensual: null }[expense.frequency] || null;
                const typeLabel = { recurrente_fijo: 'Fijo', recurrente_variable: 'Variable', recurrente_temporal: 'Temporal', puntual: 'Único' }[expense.type] || null;
                const sourceRoom = expense.room_id ? rooms.find(r => String(r.id) === String(expense.room_id)) : null;
                return (
                  <div key={expense.id} className="expense-item" style={{ opacity: expense.active === false ? 0.5 : 1 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{expense.name}</span>
                        {sourceRoom && (
                          <span style={{
                            fontSize: 10, background: '#E3F2FD', color: '#1565C0',
                            padding: '2px 6px', borderRadius: 8, fontWeight: 600, flexShrink: 0,
                          }}>
                            {sourceRoom.name}
                          </span>
                        )}
                        {expense.attachment_url && (
                          /\.(jpg|jpeg|png|gif|webp|heic|heif|avif)(\?|$)/i.test(expense.attachment_url)
                            ? (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setAttachmentLightbox(expense.attachment_url); }} title="Ver imagen" style={{ background: 'none', border: 'none', padding: 0, color: '#90CAF9', lineHeight: 1, display: 'flex', alignItems: 'center', cursor: 'zoom-in' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                              </button>
                            ) : (
                              <a href={expense.attachment_url} target="_blank" rel="noreferrer" title="Ver adjunto" onClick={e => e.stopPropagation()} style={{ color: '#90CAF9', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                              </a>
                            )
                        )}
                        {typeLabel && <span className={`frequency-badge ${expense.type}`} style={{ fontSize: 10 }}>{typeLabel}</span>}
                        {freqLabel && expense.frequency !== 'unico' && (
                          <span
                            className={`frequency-badge ${expense.frequency}`}
                            style={!isPaying ? { opacity: 0.4 } : undefined}
                          >{freqLabel}</span>
                        )}
                        {expense.type === 'recurrente_temporal' && expense.duration_payments && (
                          <span style={{ fontSize: 10, color: '#aaa' }}>({calcPaymentsMade(expense)}/{expense.duration_payments} pagos)</span>
                        )}
                        {expPct !== ownershipPct && (
                          <span style={{ fontSize: 10, color: '#888' }}>{expPct}%</span>
                        )}
                        {expense.active === false && <span style={{ fontSize: 10, color: '#aaa' }}>Pausado</span>}
                      </div>
                      {showPendingInput && (
                        <PendingVariableInput expenseId={expense.id} onSave={handleUpdateVariableAmount} />
                      )}
                    </div>
                    <div className="expense-actions" style={{ gap: 6 }}>
                      <span style={{ color: isPendingVariable ? '#FFA726' : undefined }}>
                        {isPendingVariable
                          ? '— €'
                          : expense.frequency === 'manual' && !expense.amount
                            ? '— €'
                            : `${monthly.toFixed(2)} ${expenseView === 'real' ? '€' : '€/mes'}`}
                      </span>
                      {expense.frequency === 'manual' && (
                        <PendingVariableInput expenseId={expense.id} onSave={handleUpdateVariableAmount} buttonLabel="+ Importe" />
                      )}
                      {canEdit && (
                        <ExpenseMenu
                          expenseId={expense.id}
                          openMenuId={openMenuExpenseId}
                          setOpenMenuId={setOpenMenuExpenseId}
                          onEdit={() => { setEditingExpense(expense); setOpenMenuExpenseId(null); }}
                          onDelete={() => handleDeleteExpense(expense)}
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {visibleExpenses.length > 0 && (
              <div className="expense-item total"><strong>{expenseView === 'real' ? 'Total mes' : 'Equiv. mensual'}</strong><strong>{totalExpenses.toFixed(2)} €</strong></div>
            )}
            {canEdit && <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>}
          </div>
        )}
        {!showExpenses && canEdit && (
          <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>
        )}
      </div>

      {/* Inquilinos */}
      {(property.status === 'alquilado' || property.status === 'otros') && (
        <div className="info-card tenant-card">
          <div className="card-header">
            <h3>Inquilinos</h3>
            {canEdit && (tenants.length === 0 || property.isSharedProperty) && (
              <button className="add-tenant-button" onClick={() => setShowAddTenant(true)}>
                + Añadir inquilino
              </button>
            )}
          </div>
          
          {tenants.length > 0 && (
            <div className="tenants-list">
              {tenants.map(tenant => (
                <div key={tenant.id} className="tenant-item" onClick={() => setEditingTenant(tenant)}>
                  <div className="tenant-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="tenant-item-info">
                    <p className="tenant-name">{tenant.name}</p>
                    <p className="tenant-phone">{tenant.phone}</p>
                    {property.isSharedProperty && (
                      <p className="tenant-amount-small">{tenant.amount}€/mes</p>
                    )}
                  </div>
                  <button className="tenant-chat-icon" onClick={(e) => { e.stopPropagation(); setChatWithTenant({ tenantId: tenant.id, tenantName: tenant.name, roomId: null }); }}>
                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {(unreadCounts[tenant.id] || 0) > 0 && (
                        <span style={{
                          position: 'absolute', top: -4, right: -4,
                          background: '#e74c3c', color: 'white',
                          borderRadius: '50%', width: 16, height: 16,
                          fontSize: 10, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {unreadCounts[tenant.id]}
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <MonthlyHistoryCard
        name={property.name}
        startYear={historyStart.year}
        startMonth={historyStart.month}
        computeMonth={computeHistoryMonth}
        onSelectMonth={(y, m) => { setCurrentYear(y); setCurrentMonth(m); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
      />

      {/* Datos de inversión */}
      <div className="info-card">
        <div className="card-header clickable" onClick={() => setShowInvestmentSection(!showInvestmentSection)}>
          <h3>Datos de inversión</h3>
          <span className="arrow">{showInvestmentSection ? '▼' : '›'}</span>
        </div>

        {showInvestmentSection && (
          <div style={{ padding: '4px 4px 8px' }}>
            <InvestmentForm
              property={property}
              expenses={expenses}
              onUpdate={onUpdate}
              landlordEmail={landlordEmail}
              onExpenseAdded={handleExpenseAdded}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {(showAddExpense || editingExpense) && (
        <AddExpenseModal
          onClose={() => { setShowAddExpense(false); setEditingExpense(null); }}
          onAdd={handleAddExpense}
          onUpdate={handleUpdateExpense}
          defaultExpensePct={property.ownershipPercentage || 100}
          initialExpense={editingExpense}
          landlordEmail={landlordEmail}
          propertyId={property.id}
          defaultDate={`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`}
          isPastMonth={isPastMonth}
          existingExpenses={expenses}
        />
      )}
      {showAddTenant && <AddTenantModal onClose={() => setShowAddTenant(false)} onAdd={handleAddTenant} isFirstTenant={tenants.length === 0} />}
      {showAddRoom && <AddRoomModal onClose={() => setShowAddRoom(false)} onAdd={handleAddRoom} />}
      {confirmingTenant && (
        <ConfirmPaymentModal
          defaultAmount={confirmingTenant.amount}
          tenantName={confirmingTenant.name}
          onClose={() => setConfirmingTenant(null)}
          onConfirm={(amount) => handleConfirmWithAmount(confirmingTenant.id, amount)}
        />
      )}
      {editingRoom && (
        <EditRoomModal 
          room={editingRoom}
          onClose={() => setEditingRoom(null)}
          onSave={handleEditRoom}
          onDelete={handleDeleteRoom}
          onAssignTenant={handleAssignTenantToRoom}
          onRemoveTenant={handleRemoveTenantFromRoom}
        />
      )}
      {showEditModal && (
        <EditPropertyModal property={property} onClose={() => setShowEditModal(false)}
          onSave={(updatedData) => { onUpdate({ ...property, ...updatedData }); setShowEditModal(false); }}/>
      )}
      {editingTenant && (
        <EditTenantModal
          tenant={editingTenant}
          paymentConfig={paymentConfig}
          onClose={() => setEditingTenant(null)}
          onSave={(updatedTenant, updatedConfig) => {
            handleEditTenant(updatedTenant);
            if (updatedConfig) {
              setPaymentConfig(updatedConfig);
              onUpdate({ ...property, paymentConfig: updatedConfig });
            }
          }}
          onDelete={handleDeleteTenant}
        />
      )}
      {deleteExpenseModal && (
        <DeleteExpenseModal
          expense={deleteExpenseModal.expense}
          onClose={() => setDeleteExpenseModal(null)}
          onSkipPeriod={handleSkipExpenseMonth}
          onDeletePermanently={handleDeleteExpensePermanently}
        />
      )}

      {/* Duplicate expense detection modal */}
      {duplicateExpenseModal && (
        <DuplicateExpenseModal
          data={duplicateExpenseModal}
          onClose={() => setDuplicateExpenseModal(null)}
          onExtend={handleExtendExistingExpense}
          onAddAnyway={async () => { const { newExpenseData, prevStartDates } = duplicateExpenseModal; setDuplicateExpenseModal(null); await doInsertExpense(newExpenseData, prevStartDates); }}
        />
      )}

      {/* Image lightbox for expense attachments */}
      {attachmentLightbox && (
        <div
          onClick={() => setAttachmentLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <button
            onClick={() => setAttachmentLightbox(null)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 36, cursor: 'pointer', lineHeight: 1, padding: '4px 10px' }}
          >×</button>
          <img
            src={attachmentLightbox}
            alt="Adjunto"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '95vw', maxHeight: '90dvh', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );
}


// "Delete recurring expense" dialog: skip the current period or delete permanently
function DeleteExpenseModal({ expense, onClose, onSkipPeriod, onDeletePermanently }) {
  const freq = expense.frequency;
  const customMonths = expense.custom_frequency_months || 1;
  const periodLabel = freq === 'anual' ? 'este año'
    : freq === 'trimestral' ? 'este trimestre'
    : freq === 'custom' && customMonths === 6 ? 'este semestre'
    : freq === 'custom' && customMonths === 2 ? 'este bimestre'
    : freq === 'custom' ? `estos ${customMonths} meses`
    : 'este mes';
  const buttonLabel = freq === 'anual' ? 'Solo este año'
    : freq === 'trimestral' ? 'Solo este trimestre'
    : freq === 'custom' && customMonths === 6 ? 'Solo este semestre'
    : freq === 'custom' && customMonths === 2 ? 'Solo este bimestre'
    : freq === 'custom' ? `Solo estos ${customMonths} meses`
    : 'Solo este mes';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Eliminar gasto</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ color: '#555', fontSize: '14px', margin: '0 0 20px', lineHeight: 1.5 }}>
          ¿Cómo quieres eliminar <strong>{expense.name}</strong>?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => onSkipPeriod(expense.id)}
            style={{ padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: 'white', fontSize: '15px', cursor: 'pointer', color: '#333', textAlign: 'left' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>{buttonLabel}</span>
            <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: 2 }}>
              El gasto se omite {periodLabel} pero sigue activo los demás períodos
            </span>
          </button>
          <button
            onClick={() => onDeletePermanently(expense.id)}
            style={{ padding: '12px', borderRadius: '10px', border: 'none', background: '#F44336', fontSize: '15px', cursor: 'pointer', color: 'white', textAlign: 'left' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>Definitivamente</span>
            <span style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
              Elimina el gasto de forma permanente
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Shown when a new recurring expense overlaps an existing one (same category + frequency)
function DuplicateExpenseModal({ data, onClose, onExtend, onAddAnyway }) {
  const { newExpenseData, conflict, canExtend } = data;
  const freqLabel = { mensual: 'mensual', trimestral: 'trimestral', anual: 'anual' }[newExpenseData.frequency] || newExpenseData.frequency;
  const conflictStart = new Date((conflict.start_date || '').substring(0, 10) + 'T12:00:00');
  const conflictStartLabel = conflictStart.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  const newStartLabel = new Date(newExpenseData.start_date.substring(0, 10) + 'T12:00:00').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Gasto ya existente</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <p style={{ color: '#555', fontSize: '14px', margin: '0 0 20px', lineHeight: 1.5 }}>
          Ya tienes un gasto <strong>{freqLabel}</strong> de la misma categoría desde <strong>{conflictStartLabel}</strong> ({conflict.name}).
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {canExtend && (
            <button
              onClick={onExtend}
              style={{ padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: 'white', fontSize: '15px', cursor: 'pointer', color: '#333', textAlign: 'left' }}
            >
              <span style={{ display: 'block', fontWeight: 600 }}>Adelantar el inicio a {newStartLabel}</span>
              <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: 2 }}>
                El gasto existente empieza desde antes, sin duplicados
              </span>
            </button>
          )}
          <button
            onClick={onClose}
            style={{ padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: 'white', fontSize: '15px', cursor: 'pointer', color: '#333', textAlign: 'left' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>Cancelar</span>
            <span style={{ display: 'block', fontSize: '12px', color: '#888', marginTop: 2 }}>
              No añadir el gasto
            </span>
          </button>
          <button
            onClick={onAddAnyway}
            style={{ padding: '12px', borderRadius: '10px', border: 'none', background: '#111', fontSize: '15px', cursor: 'pointer', color: 'white', textAlign: 'left' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>Añadir igualmente</span>
            <span style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
              Se crearán dos gastos separados
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function PendingVariableInput({ expenseId, onSave, buttonLabel }) {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} style={{ marginTop: 4, fontSize: 11, background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: 6, padding: '3px 8px', color: '#E65100', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {buttonLabel || (
          <>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Introducir importe
          </>
        )}
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
      <input
        type="number"
        step="0.01"
        placeholder="0.00"
        value={value}
        onChange={e => setValue(e.target.value)}
        style={{ width: 80, fontSize: 13, padding: '3px 6px', borderRadius: 6, border: '1px solid #ddd' }}
        autoFocus
      />
      <button
        onClick={() => { if (value) { onSave(expenseId, value); setEditing(false); } }}
        style={{ fontSize: 11, background: '#4CAF50', color: 'white', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}
      >
        Guardar
      </button>
      <button onClick={() => setEditing(false)} style={{ fontSize: 11, background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>×</button>
    </div>
  );
}

const EXPENSE_CATEGORIES = [
  { key: 'comunidad',    label: 'Comunidad',         subcategories: ['Cuota', 'Derrama', 'Otros'] },
  { key: 'suministros',  label: 'Suministros',        subcategories: ['Luz', 'Agua', 'Gas', 'Internet', 'Otros'] },
  { key: 'seguros',      label: 'Seguros',            subcategories: ['Hogar', 'Impago', 'Responsabilidad civil', 'Otros'] },
  { key: 'reparaciones', label: 'Reparaciones',       subcategories: ['Fontanería', 'Electricidad', 'Pintura', 'Electrodomésticos', 'Otros'] },
  { key: 'impuestos',    label: 'Impuestos',          subcategories: ['IBI', 'Basuras', 'Otros'] },
  { key: 'gestion',      label: 'Gestión',            subcategories: ['Agencia', 'Gestoría', 'Otros'] },
  { key: 'hipoteca',     label: 'Hipoteca/Préstamo',  subcategories: [] },
  { key: 'otros',        label: 'Otros',              subcategories: [] },
];


function AddExpenseModal({ onClose, onAdd, onUpdate, defaultExpensePct, initialExpense, defaultValues, landlordEmail, propertyId, defaultDate, isPastMonth, existingExpenses }) {
  const today = new Date().toISOString().split('T')[0];
  const isEditing = !!initialExpense;
  const [category, setCategory] = useState(isEditing ? (initialExpense.category || '') : (defaultValues?.category || ''));
  const [subcategory, setSubcategory] = useState(isEditing ? (initialExpense.subcategory || '') : '');
  const [description, setDescription] = useState(isEditing ? (initialExpense.description || '') : '');
  const [amount, setAmount] = useState(isEditing ? (initialExpense.amount != null ? String(initialExpense.amount) : '') : (defaultValues?.amount != null ? String(defaultValues.amount) : ''));
  const [repeats, setRepeats] = useState(isEditing ? initialExpense.frequency !== 'unico' : (defaultValues?.repeats ?? false));
  const [frequency, setFrequency] = useState(isEditing ? (initialExpense.frequency === 'unico' ? 'mensual' : initialExpense.frequency) : (defaultValues?.frequency || 'mensual'));
  const [customFreqMonths, setCustomFreqMonths] = useState(isEditing ? (initialExpense.custom_frequency_months ? String(initialExpense.custom_frequency_months) : '') : '');
  const [durationType, setDurationType] = useState(isEditing ? (initialExpense.duration_payments ? 'pagos' : 'indefinido') : 'indefinido');
  const [durationPayments, setDurationPayments] = useState(isEditing ? (initialExpense.duration_payments ? String(initialExpense.duration_payments) : '') : '');
  const [firstPaymentDate, setFirstPaymentDate] = useState('');
  const [includePastPayments, setIncludePastPayments] = useState(true);
  const [startDate, setStartDate] = useState(isEditing ? (initialExpense.start_date || today) : (defaultDate || today));
  const [expensePct, setExpensePct] = useState(isEditing ? (initialExpense.expense_percentage != null ? String(initialExpense.expense_percentage) : '') : (defaultExpensePct != null ? String(defaultExpensePct) : ''));
  const fileInputRef = React.useRef(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(isEditing ? (initialExpense.attachment_url || '') : '');
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [selectedFolderPath, setSelectedFolderPath] = useState(null); // null = no elegido, '' = raíz
  const viewedYear = defaultDate ? parseInt(defaultDate.substring(0, 4)) : new Date().getFullYear();
  const viewedMonth = defaultDate ? parseInt(defaultDate.substring(5, 7)) - 1 : new Date().getMonth();
  const maxPrevYear = viewedMonth > 0 ? viewedYear : viewedYear - 1;
  const [selectedMonths, setSelectedMonths] = useState(() => new Set());
  const [prevPickerYear, setPrevPickerYear] = useState(maxPrevYear);
  const [applyToPrev, setApplyToPrev] = useState(false);
  const [applyToPrevRecurring, setApplyToPrevRecurring] = useState(false);
  const [retroYear, setRetroYear] = useState('');
  const [retroMonth, setRetroMonth] = useState('');

  const handleCategoryChange = (val) => { setCategory(val); setSubcategory(''); };

  const handleFolderSelected = (folderPath) => {
    setAttachmentFile(pendingFile);
    setSelectedFolderPath(folderPath);
    setPendingFile(null);
    setShowFolderPicker(false);
  };

  const hasAmount = amount !== '' && parseFloat(amount) > 0;
  const derivedType = !repeats
    ? 'puntual'
    : hasAmount
      ? (durationType === 'pagos' ? 'recurrente_temporal' : 'recurrente_fijo')
      : 'recurrente_variable';

  const hasExistingRecurring = !isEditing && repeats && category
    ? (existingExpenses || []).some(e =>
        e.active !== false &&
        e.category === category &&
        e.frequency === frequency &&
        (frequency !== 'custom' || (e.custom_frequency_months || 1) === (parseInt(customFreqMonths) || 1))
      )
    : false;
  // "Aplicar a meses anteriores": the user picks the month the recurring expense really started
  const retroDate = (!isEditing && repeats && applyToPrevRecurring && retroYear !== '' && retroMonth !== ''
    && isMonthBefore(parseInt(retroYear), parseInt(retroMonth), startDate))
    ? buildRetroStartDate(parseInt(retroYear), parseInt(retroMonth), startDate)
    : null;
  const retroMonths = retroDate
    ? calcRetroMonths(retroDate, startDate, frequency, customFreqMonths,
        durationType === 'pagos' ? (parseInt(durationPayments) || undefined) : undefined)
    : [];
  const startYearNum = parseInt(startDate.substring(0, 4)) || new Date().getFullYear();
  const retroYearOptions = Array.from({ length: startYearNum - 1999 }, (_, i) => startYearNum - i);
  const enableRetro = () => {
    setApplyToPrevRecurring(true);
    if (retroYear === '' || retroMonth === '') {
      const d = defaultRetroMonth(startDate, frequency, customFreqMonths);
      setRetroYear(String(d.year));
      setRetroMonth(String(d.month));
    }
  };
  const freqStep = frequency === 'trimestral' ? 3 : frequency === 'anual' ? 12 : frequency === 'custom' ? (parseInt(customFreqMonths) || 1) : 1;
  const freqLabel = frequency === 'trimestral' ? 'trimestre' : frequency === 'anual' ? 'año' : frequency === 'custom' ? `${customFreqMonths || '?'} meses` : 'mes';
  const monthlyEquiv = amount && repeats && frequency !== 'mensual' ? (parseFloat(amount) / freqStep).toFixed(2) : null;

  // Fixed-duration expenses: if the first payment was earlier (e.g. an old mortgage), only count what's left
  // First payment before the start month (e.g. mortgage start date): either add the
  // payments already made (expense starts on that date, like "Datos de inversión")
  // or start now and count only the remaining ones.
  const firstPaymentInPast = !isEditing && repeats && durationType === 'pagos' && !applyToPrevRecurring
    && !!firstPaymentDate && isMonthBefore(parseInt(firstPaymentDate.substring(0, 4)), parseInt(firstPaymentDate.substring(5, 7)) - 1, startDate);
  const backfillFromFirstPayment = firstPaymentInPast && includePastPayments;
  const effectiveStart = backfillFromFirstPayment
    ? firstPaymentDate
    : (!isEditing && repeats && applyToPrevRecurring && retroDate) ? retroDate : startDate;
  const remainingPayments = repeats && durationType === 'pagos' && durationPayments
    ? calcRemainingPayments(durationPayments, retroDate ? '' : firstPaymentDate, effectiveStart, frequency, customFreqMonths)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && repeats && applyToPrevRecurring && !retroDate) {
      alert('Elige un mes anterior a la fecha de inicio para aplicar el gasto a meses anteriores.');
      return;
    }
    if (remainingPayments !== null && remainingPayments < 1) {
      alert('Según la fecha del primer pago, ya se han hecho todos los pagos.');
      return;
    }
    let finalAttachmentUrl = attachmentUrl;
    if (attachmentFile) {
      setUploading(true);
      try {
        const safeName = attachmentFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        // selectedFolderPath: null = picker no usado (fallback gastos), '' = raíz, 'X' = carpeta X
        const uploadPath = selectedFolderPath !== null
          ? `${landlordEmail}/${propertyId}${selectedFolderPath ? '/' + selectedFolderPath : ''}/${Date.now()}_${safeName}`
          : `${landlordEmail}/gastos/${propertyId}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage.from('documentos').upload(uploadPath, attachmentFile, { upsert: false });
        if (upErr) {
          if (upErr.message?.includes('Bucket not found') || upErr.statusCode === 404 || upErr.error === 'Bucket not found') {
            throw new Error('El bucket "documentos" no existe en Supabase Storage.\n\nCómo crearlo:\n1. Ve al dashboard de Supabase\n2. Entra en Storage → New Bucket\n3. Nombre: "documentos"\n4. Activa "Public bucket"\n5. Haz clic en "Create bucket"');
          }
          throw upErr;
        }
        finalAttachmentUrl = supabase.storage.from('documentos').getPublicUrl(uploadPath).data.publicUrl;
      } catch (err) {
        alert(`Error subiendo el archivo: ${err.message}`);
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const catObj = EXPENSE_CATEGORIES.find(c => c.key === category);
    const parts = [catObj?.label || category];
    if (subcategory) parts.push(subcategory);
    if (description) parts.push(description);
    const expenseData = {
      name: parts.join(' — '),
      category,
      subcategory: subcategory || null,
      description,
      type: derivedType,
      frequency: !repeats ? 'unico' : frequency,
      custom_frequency_months: frequency === 'custom' && repeats ? (parseInt(customFreqMonths) || null) : null,
      amount: hasAmount ? parseFloat(amount) : null,
      duration_payments: remainingPayments,
      start_date: startDate,
      active: true,
      expense_percentage: expensePct ? parseFloat(expensePct) : null,
      attachment_url: finalAttachmentUrl || null,
    };
    if (isEditing) {
      onUpdate(initialExpense.id, expenseData);
    } else {
      const prevDates = applyToPrev ? Array.from(selectedMonths).sort().map(ym => `${ym}-01`) : [];
      const finalData = backfillFromFirstPayment
        ? { ...expenseData, start_date: firstPaymentDate }
        : (repeats && applyToPrevRecurring && retroDate)
          ? { ...expenseData, start_date: retroDate }
          : expenseData;
      onAdd(finalData, prevDates.length > 0 ? prevDates : undefined);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{isEditing ? 'Editar gasto' : 'Añadir gasto'}</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Categoría</label>
            <select value={category} onChange={e => handleCategoryChange(e.target.value)} required
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', background: 'white', color: category ? '#111' : '#aaa' }}>
              <option value="" disabled>Selecciona una categoría</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {category && (EXPENSE_CATEGORIES.find(c => c.key === category)?.subcategories?.length > 0) && (
            <div className="form-group">
              <label>Subcategoría</label>
              <div className="frequency-options subcategory-options">
                {EXPENSE_CATEGORIES.find(c => c.key === category).subcategories.map(s => (
                  <button key={s} type="button"
                    className={`frequency-option ${subcategory === s ? 'selected' : ''}`}
                    onClick={() => setSubcategory(s === subcategory ? '' : s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="form-group">
              <label>¿Se repite?</label>
              <div className="frequency-options">
                <button type="button" className={`frequency-option ${!repeats ? 'selected' : ''}`} onClick={() => setRepeats(false)}>No</button>
                <button type="button" className={`frequency-option ${repeats ? 'selected' : ''}`} onClick={() => setRepeats(true)}>Sí</button>
              </div>
            </div>
          {repeats && (
            <>
              <div className="form-group">
                <label>Frecuencia</label>
                <div className="frequency-options frequency-grid">
                  {['mensual', 'trimestral', 'anual', 'custom'].map(f => (
                    <button key={f} type="button"
                      className={`frequency-option ${frequency === f ? 'selected' : ''}`}
                      onClick={() => setFrequency(f)}>
                      {f === 'mensual' ? 'Mensual' : f === 'trimestral' ? 'Trimestral' : f === 'anual' ? 'Anual' : 'Otra'}
                    </button>
                  ))}
                </div>
                {frequency === 'custom' && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>Cada</span>
                    <input type="number" min="1" placeholder="2" value={customFreqMonths}
                      onChange={e => setCustomFreqMonths(e.target.value)} required
                      style={{ width: 70, padding: '8px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, textAlign: 'center' }} />
                    <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>meses</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Duración</label>
                <div className="frequency-options">
                  <button type="button" className={`frequency-option ${durationType === 'indefinido' ? 'selected' : ''}`} onClick={() => setDurationType('indefinido')}>Indefinido</button>
                  <button type="button" className={`frequency-option ${durationType === 'pagos' ? 'selected' : ''}`} onClick={() => setDurationType('pagos')}>Nº de pagos</button>
                </div>
                {durationType === 'pagos' && (
                  <div style={{ marginTop: 10 }}>
                    <input type="number" min="1" placeholder="Ej: 12" value={durationPayments}
                      onChange={e => setDurationPayments(e.target.value)} required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                    {!applyToPrevRecurring && (
                      <>
                      <label style={{ marginTop: 14 }}>{category === 'hipoteca' ? 'Inicio de la hipoteca (opcional)' : 'Fecha del primer pago (opcional)'}</label>
                      <input type="date" value={firstPaymentDate} max={startDate}
                        onChange={e => setFirstPaymentDate(e.target.value)} />
                      {!firstPaymentInPast && (
                        <p style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
                          Si empezaste a pagar antes, indica cuándo.
                        </p>
                      )}
                      {firstPaymentInPast && (
                        <>
                          <label style={{ marginTop: 14 }}>¿Añadir también los pagos ya hechos?</label>
                          <div className="frequency-options">
                            <button type="button" className={`frequency-option ${includePastPayments ? 'selected' : ''}`}
                              onClick={() => setIncludePastPayments(true)}>Sí</button>
                            <button type="button" className={`frequency-option ${!includePastPayments ? 'selected' : ''}`}
                              onClick={() => setIncludePastPayments(false)}>No</button>
                          </div>
                          <p style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
                            {includePastPayments
                              ? `La cuota aparecerá en todos los meses desde ${category === 'hipoteca' ? 'el inicio de la hipoteca' : 'el primer pago'}.`
                              : 'La cuota aparecerá desde la fecha de inicio, contando solo los pagos que quedan.'}
                          </p>
                        </>
                      )}
                      </>
                    )}
                    {remainingPayments !== null && (
                      <p className="monthly-equivalent">
                        {remainingPayments < 1
                          ? 'Según esta fecha ya se han hecho todos los pagos'
                          : `${remainingPayments !== parseInt(durationPayments) ? `Quedan ${remainingPayments} de ${durationPayments} pagos · ` : ''}Último pago: ${calcLastPaymentLabel(remainingPayments, effectiveStart, frequency, customFreqMonths)}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {repeats && !isEditing && (
            <div className="form-group">
              <label>¿Aplicar a meses anteriores?</label>
              <div className="frequency-options">
                <button type="button"
                  className={`frequency-option ${!applyToPrevRecurring ? 'selected' : ''}`}
                  onClick={() => setApplyToPrevRecurring(false)}>No</button>
                <button type="button"
                  className={`frequency-option ${applyToPrevRecurring ? 'selected' : ''}`}
                  onClick={enableRetro}>Sí</button>
              </div>
              {applyToPrevRecurring && (
                <div style={{ marginTop: 12 }}>
                  <label>Desde</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <select value={retroMonth} onChange={e => setRetroMonth(e.target.value)}
                      style={{ flex: 3, minWidth: 0, padding: '12px', borderRadius: 10, border: '1px solid #ddd', fontSize: 15, background: 'white' }}>
                      {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((label, idx) => (
                        <option key={idx} value={idx}
                          disabled={retroYear !== '' && !isMonthBefore(parseInt(retroYear), idx, startDate)}>{label}</option>
                      ))}
                    </select>
                    <select value={retroYear} onChange={e => setRetroYear(e.target.value)}
                      style={{ flex: 2, minWidth: 0, padding: '12px', borderRadius: 10, border: '1px solid #ddd', fontSize: 15, background: 'white' }}>
                      {retroYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  {!retroDate && (
                    <p style={{ fontSize: 12, color: '#C62828', marginTop: 6 }}>Elige un mes anterior a la fecha de inicio.</p>
                  )}
                  {retroDate && retroMonths.length > 0 && (
                    <p className="monthly-equivalent">
                      {retroMonths.length <= 6
                        ? `Se añadirá también en: ${retroMonths.join(', ')}`
                        : `Se añadirán ${retroMonths.length} pagos anteriores: ${retroMonths[0]} – ${retroMonths[retroMonths.length - 1]}`}
                    </p>
                  )}
                </div>
              )}
              {applyToPrevRecurring && hasExistingRecurring && (
                <p style={{ fontSize: 12, color: '#B26A00', marginTop: 6, lineHeight: 1.4 }}>
                  Ya tienes un gasto de esta categoría con la misma frecuencia. Si ya cubre esos meses, se duplicará.
                </p>
              )}
            </div>
          )}
          <div className="form-group">
            <label>Importe por {!repeats ? 'pago' : freqLabel} (€){repeats ? ' — opcional si varía' : ''}</label>
            <input type="number" placeholder={repeats ? '0.00 (deja en blanco si varía)' : '0.00'}
              value={amount} onChange={e => setAmount(e.target.value)}
              required={!repeats} step="0.01" min="0" />
            {monthlyEquiv && <p className="monthly-equivalent">Equivalente mensual: {monthlyEquiv} €/mes</p>}
            {repeats && !hasAmount && <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Introduces el importe cuando llega la factura.</p>}
          </div>
          <div className="form-group">
            <label>{!repeats ? 'Fecha del gasto' : 'Fecha de inicio'}</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
            {isPastMonth && !isEditing && repeats && (
              <p style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
                El gasto empezará en esta fecha y aparecerá en el mes actual y futuros según la frecuencia elegida.
              </p>
            )}
          </div>
          <div className="form-group">
            <label>% de titularidad</label>
            <input type="number" placeholder={`${defaultExpensePct ?? 100}`} value={expensePct}
              onChange={e => setExpensePct(e.target.value)} step="0.01" min="0" max="100" />
          </div>
          <div className="form-group">
            <label>Descripción (opcional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="text" placeholder="Ej: recibo gas, fontanero..." value={description}
                onChange={e => setDescription(e.target.value)} style={{ flex: 1 }} />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                setPendingFile(file);
                setShowFolderPicker(true);
                e.target.value = '';
              }}
              />
              <button
                type="button"
                title="Adjuntar imagen o PDF"
                onClick={() => fileInputRef.current?.click()}
                style={{ background: attachmentFile ? '#E3F2FD' : 'none', border: '1px solid #ddd', borderRadius: 8, padding: '10px', cursor: 'pointer', color: attachmentFile ? '#1976D2' : '#aaa', lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
            </div>
            {attachmentFile && (
              <p style={{ fontSize: 11, color: '#555', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                {attachmentFile.name}
              </p>
            )}
            {!attachmentFile && attachmentUrl && (
              <p style={{ fontSize: 11, marginTop: 4 }}>
                <a href={attachmentUrl} target="_blank" rel="noreferrer" style={{ color: '#2196F3' }}>Ver archivo adjunto actual</a>
                <button type="button" onClick={() => setAttachmentUrl('')} style={{ marginLeft: 8, fontSize: 10, background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>Quitar</button>
              </p>
            )}
            {uploading && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Subiendo archivo…</p>}
          </div>
          {isPastMonth && !isEditing && !repeats && (
            <div className="form-group">
              <label>¿Aplicar a meses anteriores?</label>
              <div className="frequency-options" style={{ marginBottom: applyToPrev ? 10 : 0 }}>
                <button type="button" className={`frequency-option ${!applyToPrev ? 'selected' : ''}`} onClick={() => { setApplyToPrev(false); setSelectedMonths(new Set()); }}>No</button>
                <button type="button" className={`frequency-option ${applyToPrev ? 'selected' : ''}`} onClick={() => setApplyToPrev(true)}>Sí</button>
              </div>
              {applyToPrev && <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <button type="button"
                    onClick={() => setPrevPickerYear(y => y - 1)}
                    disabled={prevPickerYear <= 2020}
                    style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#555', padding: '2px 10px', lineHeight: 1 }}>
                    ‹
                  </button>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{prevPickerYear}</span>
                  <button type="button"
                    onClick={() => setPrevPickerYear(y => y + 1)}
                    disabled={prevPickerYear >= maxPrevYear}
                    style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#555', padding: '2px 10px', lineHeight: 1 }}>
                    ›
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'].map((label, m) => {
                    const key = `${prevPickerYear}-${String(m + 1).padStart(2, '0')}`;
                    const disabled = prevPickerYear > viewedYear
                      || (prevPickerYear === viewedYear && m >= viewedMonth)
                      || prevPickerYear < 2020;
                    const isSelected = selectedMonths.has(key);
                    return (
                      <button key={m} type="button" disabled={disabled}
                        onClick={() => {
                          setSelectedMonths(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                          });
                        }}
                        style={{
                          padding: '8px 4px', borderRadius: 8,
                          border: isSelected ? '2px solid #111' : '1px solid #ddd',
                          background: isSelected ? '#111' : disabled ? '#f5f5f5' : 'white',
                          color: isSelected ? 'white' : disabled ? '#ccc' : '#333',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          fontSize: 13, fontWeight: isSelected ? 600 : 400,
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {selectedMonths.size > 0 && (
                  <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                    {selectedMonths.size} {selectedMonths.size === 1 ? 'mes seleccionado' : 'meses seleccionados'}
                  </p>
                )}
              </div>}
            </div>
          )}
          <button type="submit" className="submit-button" disabled={uploading}>{isEditing ? 'Guardar cambios' : 'Añadir gasto'}</button>
        </form>
      </div>
      {showFolderPicker && (
        <FolderPickerModal
          landlordEmail={landlordEmail}
          propertyId={propertyId}
          onClose={() => { setShowFolderPicker(false); setPendingFile(null); }}
          onConfirm={handleFolderSelected}
        />
      )}
    </div>
  );
}

function FolderPickerModal({ landlordEmail, propertyId, onClose, onConfirm }) {
  const [allFolders, setAllFolders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentView, setCurrentView] = React.useState('');
  const [selectedPath, setSelectedPath] = React.useState('');
  const [showNewFolder, setShowNewFolder] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    supabase.from('folders').select('*')
      .eq('landlord_email', landlordEmail)
      .eq('property_id', propertyId)
      .then(({ data }) => { setAllFolders(data || []); setLoading(false); });
  }, [landlordEmail, propertyId]);

  const getDirectChildren = (parentPath) =>
    allFolders.filter(f => {
      if (!parentPath) return !f.path.includes('/');
      const prefix = parentPath + '/';
      return f.path.startsWith(prefix) && !f.path.slice(prefix.length).includes('/');
    });

  const breadcrumbs = ['Inicio', ...currentView.split('/').filter(Boolean)];

  const navigateTo = (idx) => {
    if (idx === 0) setCurrentView('');
    else {
      const parts = currentView.split('/').filter(Boolean);
      setCurrentView(parts.slice(0, idx).join('/'));
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const newPath = currentView ? `${currentView}/${newFolderName.trim()}` : newFolderName.trim();
    const { data, error } = await supabase.from('folders').insert({
      landlord_email: landlordEmail,
      property_id: propertyId,
      path: newPath,
      name: newFolderName.trim(),
    }).select().single();
    if (!error && data) {
      setAllFolders(prev => [...prev, data]);
      setSelectedPath(newPath);
    }
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const directFolders = getDirectChildren(currentView);
  const confirmLabel = !selectedPath
    ? 'Guardar en la raíz'
    : `Guardar en "${allFolders.find(f => f.path === selectedPath)?.name || selectedPath}"`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content folder-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>¿Dónde guardar?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Breadcrumb */}
        <div className="picker-breadcrumb">
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="picker-sep">›</span>}
              <button
                className={`picker-crumb${idx === breadcrumbs.length - 1 ? ' active' : ''}`}
                onClick={() => navigateTo(idx)}
              >{crumb}</button>
            </React.Fragment>
          ))}
        </div>

        {/* Save here option */}
        <button
          className={`picker-here-btn${selectedPath === currentView ? ' selected' : ''}`}
          onClick={() => setSelectedPath(currentView)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <polyline points="9 22 9 12 15 12 15 22"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Guardar aquí
          {selectedPath === currentView && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 'auto' }}>
              <polyline points="20 6 9 17 4 12" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Folder list */}
        {loading ? (
          <p style={{ textAlign: 'center', color: '#aaa', padding: '16px 0' }}>Cargando carpetas…</p>
        ) : (
          <div className="picker-folder-list">
            {directFolders.map(f => (
              <div key={f.id} className={`picker-folder-row${selectedPath === f.path ? ' selected' : ''}`}>
                <button className="picker-folder-select" onClick={() => setSelectedPath(f.path)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"
                      fill="#FFF3E0" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{f.name}</span>
                  {selectedPath === f.path && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button className="picker-enter-btn" onClick={() => setCurrentView(f.path)} title="Entrar en carpeta">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <polyline points="9 18 15 12 9 6" stroke="#bbb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}

            {showNewFolder ? (
              <div className="picker-new-folder-row">
                <input
                  autoFocus
                  type="text"
                  placeholder="Nombre de carpeta"
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreateFolder();
                    if (e.key === 'Escape') setShowNewFolder(false);
                  }}
                />
                <button onClick={handleCreateFolder}>OK</button>
                <button onClick={() => setShowNewFolder(false)}>×</button>
              </div>
            ) : (
              <button className="picker-add-folder-btn" onClick={() => setShowNewFolder(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Nueva carpeta aquí
              </button>
            )}
          </div>
        )}

        <button className="submit-button" style={{ marginTop: 16 }} onClick={() => onConfirm(selectedPath)}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

function AddTenantModal({ onClose, onAdd, isFirstTenant }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [isShared, setIsShared] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFirstTenant && !isShared) {
      onAdd({ name, email, phone, isShared: false });
    } else {
      onAdd({ name, email, phone, amount: parseFloat(amount), isShared: isFirstTenant ? true : undefined });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Añadir inquilino</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo</label>
            <input type="text" placeholder="Ej: Laura Martínez" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Email del inquilino <span style={{ fontWeight: 400, color: '#999' }}>(opcional)</span></label>
            <input type="email" placeholder="laura@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Teléfono <span style={{ fontWeight: 400, color: '#999' }}>(opcional)</span></label>
            <input type="tel" placeholder="622 280 559" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>
          {isFirstTenant && (
            <div className="form-group">
              <label>¿Es compartido?</label>
              <div className="shared-options">
                <button type="button" className={`shared-option ${!isShared ? 'selected' : ''}`} onClick={() => setIsShared(false)}>No, inquilino único</button>
                <button type="button" className={`shared-option ${isShared ? 'selected' : ''}`} onClick={() => setIsShared(true)}>Sí, más de un inquilino</button>
              </div>
              {!isShared && (
                <p className="payment-range-note" style={{ marginTop: '8px' }}>El inquilino pagará el alquiler completo</p>
              )}
            </div>
          )}
          {(!isFirstTenant || isShared) && (
            <div className="form-group">
              <label>Cantidad que paga (€/mes)</label>
              <input type="number" placeholder="300" value={amount} onChange={(e) => setAmount(e.target.value)} required step="0.01" />
            </div>
          )}
          <button type="submit" className="submit-button">Añadir inquilino</button>
        </form>
      </div>
    </div>
  );
}

function EditTenantModal({ tenant, paymentConfig, onClose, onSave, onDelete }) {
  const [name, setName] = useState(tenant.name);
  const [phone, setPhone] = useState(formatPhone(tenant.phone || ''));
  const [amount, setAmount] = useState(tenant.amount?.toString() || '');
  const [paymentStartDay, setPaymentStartDay] = useState(paymentConfig?.startDay?.toString() || '1');
  const [paymentEndDay, setPaymentEndDay] = useState(paymentConfig?.endDay?.toString() || '5');
  
  const handleSubmit = (e) => { 
    e.preventDefault(); 
    if (paymentConfig) {
      onSave({
        ...tenant,
        name,
        phone,
        amount: parseFloat(amount)
      }, {
        startDay: parseInt(paymentStartDay),
        endDay: parseInt(paymentEndDay),
        limitDay: parseInt(paymentEndDay)
      });
    } else {
      onSave({ ...tenant, name, phone, amount: parseFloat(amount) });
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Editar inquilino</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Teléfono</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>
          <div className="form-group">
            <label>Cantidad que paga (€/mes)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required step="0.01" />
          </div>
          <div className="form-group">
            <label>Rango de días para pagar el alquiler</label>
            <div className="payment-range-inputs">
              <div className="payment-day-input">
                <label>Del día</label>
                <input type="number" min="1" max="28" value={paymentStartDay} onChange={(e) => setPaymentStartDay(e.target.value)} required />
              </div>
              <span className="range-separator">—</span>
              <div className="payment-day-input">
                <label>Al día</label>
                <input type="number" min="1" max="31" value={paymentEndDay} onChange={(e) => setPaymentEndDay(e.target.value)} required />
              </div>
            </div>
            <p className="payment-range-note">El inquilino deberá marcar el pago entre el día {paymentStartDay} y {paymentEndDay} de cada mes</p>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="submit-button">Guardar cambios</button>
            <button type="button" className="delete-button" onClick={() => onDelete(tenant.id)}>Eliminar inquilino</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPropertyModal({ property, onClose, onSave }) {
  const [name, setName] = useState(property.name);
  const [price, setPrice] = useState(property.price ? property.price.toString() : '');
  const [status, setStatus] = useState(property.status);
  const [ownershipPercentage, setOwnershipPercentage] = useState(property.ownershipPercentage?.toString() || '100');

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      price: parseInt(price) || 0,
      status,
      ownershipPercentage: parseFloat(ownershipPercentage),
    });
  };

  const STATUS_OPTIONS = [
    ['vacio', 'Vacío'],
    ['alquilado', 'Alquilado'],
    ['por_habitaciones', 'Por habitaciones'],
    ['vacacional', 'Vacacional'],
    ['otros', 'Otros'],
    ['uso_propio', 'Uso propio'],
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Editar propiedad</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre/Dirección</label>
            <input type="text" placeholder="Ej: Calle Mayor 12 · 2°B" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Precio mensual (€)</label>
            <input type="number" placeholder="850" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Mi porcentaje de propiedad (%)</label>
            <input type="number" placeholder="100" min="1" max="100" value={ownershipPercentage} onChange={(e) => setOwnershipPercentage(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Estado</label>
            <div className="status-options-grid">
              {STATUS_OPTIONS.map(([s, label]) => (
                <button key={s} type="button" className={`status-option ${status === s ? 'selected' : ''}`} onClick={() => handleStatusChange(s)}>
                  <span className={`status-dot ${s}`}></span>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" className="submit-button">Guardar cambios</button>
        </form>
      </div>
    </div>
  );
}

function AddRoomModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({ name, price: parseFloat(price) });
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Añadir habitación</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre de la habitación</label>
            <input type="text" placeholder="Ej: Habitación 1, Suite principal..." value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Precio mensual (€)</label>
            <input type="number" placeholder="350" value={price} onChange={(e) => setPrice(e.target.value)} required step="0.01" />
          </div>
          <button type="submit" className="submit-button">Añadir habitación</button>
        </form>
      </div>
    </div>
  );
}

function EditRoomModal({ room, onClose, onSave, onDelete, onAssignTenant, onRemoveTenant }) {
  const [name, setName] = useState(room.name);
  const [price, setPrice] = useState(room.price.toString());
  const [showAddTenant, setShowAddTenant] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...room, name, price: parseFloat(price) });
  };
  
  const handleAddTenant = (tenantData) => {
    onAssignTenant(room.id, { ...tenantData, id: Date.now().toString() });
    setShowAddTenant(false);
    onClose();
  };
  
  return (
    <>
      {!showAddTenant ? (
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Editar habitación</h2><button className="modal-close" onClick={onClose}>×</button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre de la habitación</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Precio mensual (€)</label>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required step="0.01" />
              </div>
              {room.tenant ? (
                <div className="form-group">
                  <label>Inquilino actual</label>
                  <div className="current-tenant-info">
                    <div>
                      <p className="tenant-name">{room.tenant.name}</p>
                      <p className="tenant-phone">{room.tenant.phone}</p>
                    </div>
                    <button type="button" className="delete-button" onClick={() => {
                      if (window.confirm('¿Eliminar inquilino de esta habitación?')) {
                        onRemoveTenant(room.id);
                        onClose();
                      }
                    }}>
                      Quitar inquilino
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="add-tenant-button" onClick={() => setShowAddTenant(true)} style={{marginBottom: '16px'}}>
                  + Asignar inquilino
                </button>
              )}
              <div className="modal-buttons">
                <button type="submit" className="submit-button">Guardar cambios</button>
                <button type="button" className="delete-button" onClick={() => onDelete(room.id)}>Eliminar habitación</button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <AddTenantToRoomModal onClose={() => setShowAddTenant(false)} onAdd={handleAddTenant} />
      )}
    </>
  );
}

function AddTenantToRoomModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({ name, phone });
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>Asignar inquilino</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo</label>
            <input type="text" placeholder="Ej: Laura Martínez" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Teléfono</label>
            <input type="tel" placeholder="622 280 559" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
          </div>
          <button type="submit" className="submit-button">Asignar inquilino</button>
        </form>
      </div>
    </div>
  );
}

function ConfirmPaymentModal({ defaultAmount, tenantName, onClose, onConfirm }) {
  const [amount, setAmount] = useState(defaultAmount?.toString() || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(parseFloat(amount));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirmar pago</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {tenantName && (
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px', marginTop: '-8px' }}>
              Inquilino: <strong>{tenantName}</strong>
            </p>
          )}
          <div className="form-group">
            <label>Importe recibido (€)</label>
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.01"
              min="0"
              required
              autoFocus
            />
            {parseFloat(amount) !== defaultAmount && parseFloat(amount) > 0 && (
              <p style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>
                Precio base: {defaultAmount} € — estás registrando un importe diferente
              </p>
            )}
          </div>
          <button type="submit" className="submit-button">
            Confirmar pago
          </button>
        </form>
      </div>
    </div>
  );
}

export default PropertyDetail;

/*
 * SQL para ejecutar en Supabase (opcional — los datos ya se persisten en la columna JSONB `data`
 * como `investmentData`. Estas columnas dedicadas permiten consultas analíticas directas):
 *
 * ALTER TABLE properties
 *   ADD COLUMN IF NOT EXISTS purchase_price        NUMERIC,
 *   ADD COLUMN IF NOT EXISTS acquisition_costs     NUMERIC,
 *   ADD COLUMN IF NOT EXISTS ownership_percentage  NUMERIC DEFAULT 100,
 *   ADD COLUMN IF NOT EXISTS monthly_mortgage      NUMERIC,
 *   ADD COLUMN IF NOT EXISTS monthly_amortization  NUMERIC;
 *
 * -- Para rellenar las columnas desde los datos JSONB existentes:
 * UPDATE properties
 * SET
 *   purchase_price        = (data->'investmentData'->>'purchasePrice')::NUMERIC,
 *   acquisition_costs     = (data->'investmentData'->>'acquisitionCosts')::NUMERIC,
 *   ownership_percentage  = COALESCE((data->'investmentData'->>'ownershipPercentage')::NUMERIC, 100),
 *   monthly_mortgage      = (data->'investmentData'->>'monthlyMortgage')::NUMERIC,
 *   monthly_amortization  = (data->'investmentData'->>'monthlyAmortization')::NUMERIC
 * WHERE data->'investmentData' IS NOT NULL;
 *
 * -- Campo skipped_months para omitir meses concretos en gastos recurrentes:
 * ALTER TABLE expenses
 *   ADD COLUMN IF NOT EXISTS skipped_months TEXT[] DEFAULT '{}';
 */

// Shared with VacationalDetail so both pages handle expenses the same way
export { AddExpenseModal, ExpenseMenu, PendingVariableInput, DeleteExpenseModal, DuplicateExpenseModal, calcPaymentsMade };
